import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { readExcelFile, readExcelFromBase64, buildHierarchy, parseExcelSheet } from '../../services/excelService';
import { mapTilerKey, googleMapsApiKey } from '../Constants';
import styles from './ExampleDashboard.module.css';
import jsPDF from 'jspdf';

// DB servisi - Admin sayfasındaki gibi import
import { databaseService } from '../../services/databaseService';

// DB'den park verilerini çekme fonksiyonu
const fetchDbParks = async () => {
  if (!databaseService) {
    console.log('Database service not available');
    return [];
  }
  
  try {
    // Önce park_area tablosunu bul
    const schemas = await databaseService.listSchemas();
    console.log(' Şemalar:', schemas);
    
    let parksData = [];
    
    // parks şemasını kontrol et
    if (schemas.includes('parks')) {
      const tables = await databaseService.listTables('parks');
      console.log(' Parks şemasındaki tablolar:', tables);
      
      // park_area veya benzer tabloyu bul
      const parkTable = tables.find(t => 
        t.toLowerCase().includes('park') && 
        (t.toLowerCase().includes('area') || t.toLowerCase().includes('alan'))
      ) || tables.find(t => t.toLowerCase().includes('park'));
      
      if (parkTable) {
        console.log(` Tablo bulundu: parks.${parkTable}`);
        const data = await databaseService.getTableData('parks', parkTable, 1000, 0);
        parksData = data.rows || [];
        
        // Ankara ve Çankaya filtresi
        parksData = parksData.filter(park => {
          const il = park.il || park.il_adi || park.province || '';
          const ilce = park.ilce || park.ilce_adi || park.district || '';
          return il.toLowerCase().includes('ankara') && ilce.toLowerCase().includes('çankaya');
        });
      }
    }
    
    // Eğer parks şemasında bulunamadıysa, public şemasını kontrol et
    if (parksData.length === 0 && schemas.includes('public')) {
      const tables = await databaseService.listTables('public');
      const parkTable = tables.find(t => 
        t.toLowerCase().includes('park') && 
        (t.toLowerCase().includes('area') || t.toLowerCase().includes('alan'))
      );
      
      if (parkTable) {
        console.log(` Tablo bulundu: public.${parkTable}`);
        const data = await databaseService.getTableData('public', parkTable, 1000, 0);
        parksData = (data.rows || []).filter(park => {
          const il = park.il || park.il_adi || park.province || '';
          const ilce = park.ilce || park.ilce_adi || park.district || '';
          return il.toLowerCase().includes('ankara') && ilce.toLowerCase().includes('çankaya');
        });
      }
    }
    
    console.log(` ${parksData.length} park DB'den yüklendi`);
    return parksData;
  } catch (error) {
    console.error(' DB park yükleme hatası:', error);
    return [];
  }
};

// MapLibre GL'i CDN'den yükle
const maplibregl = window.maplibregl;

const ExampleDashboard = () => {
  const { user, userRole, logout } = useAuth();
  const [excelData, setExcelData] = useState(null);
  const [hierarchy, setHierarchy] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Seçim kümesi state'leri
  const [selectedIl, setSelectedIl] = useState('Ankara'); // Her zaman Ankara
  const [selectedIlce, setSelectedIlce] = useState('Çankaya'); // Her zaman Çankaya
  const [selectedMahalle, setSelectedMahalle] = useState(''); // Kullanılmıyor
  const [selectedBolge, setSelectedBolge] = useState(''); // Adres listesinden alınacak
  const [selectedParks, setSelectedParks] = useState([]);
  const [dbParks, setDbParks] = useState([]); // DB'den gelen parklar
  const [matchedParks, setMatchedParks] = useState([]); // Eşleştirilmiş parklar
  const [showOtherMenu, setShowOtherMenu] = useState(false);
  const [dbConnected, setDbConnected] = useState(false); // DB bağlantı durumu
  
  // Seçim yapıldı mı?
  const [hasSelection, setHasSelection] = useState(false);
  
  // Aktif section (seçim sonrası)
  const [activeSection, setActiveSection] = useState('overview');
  
  // Harita referansı
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const googleMapRef = useRef(null);
  const googleMapContainerRef = useRef(null);
  const [showMap, setShowMap] = useState(false);
  const [selectedParkForMap, setSelectedParkForMap] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  
  // Para hesabı için litre başına fiyat (TL/L) - Settings'ten ayarlanabilir
  const [waterPricePerLiter, setWaterPricePerLiter] = useState(() => {
    const saved = localStorage.getItem('waterPricePerLiter');
    return saved ? parseFloat(saved) : 0.05; // Varsayılan: 0.05 TL/L
  });

  // Tooltip state'leri
  const [tooltip, setTooltip] = useState({ show: false, value: '', x: 0, y: 0, label: '' });

  // Excel dosyasını yükle
  useEffect(() => {
    const loadExcelData = async () => {
      try {
        setLoading(true);
        console.log(' Excel dosyası yükleniyor...');
        
        // Excel dosyasını oku - önce base64'den, sonra fetch ile dene
        let data;
        let successfulPath = null;
        
        try {
          // Önce base64'den okumayı dene (en garantili yöntem)
          console.log('Excel dosyası base64 string\'den okunuyor...');
          data = await readExcelFromBase64();
          successfulPath = 'base64 (embedded)';
          console.log('Excel dosyası base64\'den başarıyla yüklendi');
        } catch (base64Error) {
          console.warn('Base64 okuma başarısız, fetch ile deneniyor...', base64Error.message);
          
          // Base64 başarısız olursa fetch ile dene
          const baseUrl = window.location.origin;
          const basePath = window.location.pathname.split('/').slice(0, -1).join('/') || '';
          const publicUrl = process.env.PUBLIC_URL || '';
          
          const possiblePaths = [
            '/static/2025.xlsx',
            '/2025.xlsx',
            `${publicUrl}/2025.xlsx`,
            `${publicUrl}/static/2025.xlsx`,
            './2025.xlsx',
            '2025.xlsx',
            `${basePath}/2025.xlsx`,
            `${basePath}/static/2025.xlsx`,
            `${baseUrl}/static/2025.xlsx`,
            `${baseUrl}/2025.xlsx`,
            `${baseUrl}${basePath}/2025.xlsx`,
            `${baseUrl}${publicUrl}/2025.xlsx`,
            '/2025-YENİ BÖLGE LİSTELERİ.xlsx',
            '/2025-YENI BOLGE LISTELERI.xlsx',
            '/static/2025-YENI BOLGE LISTELERI.xlsx',
            `${publicUrl}/2025-YENI BOLGE LISTELERI.xlsx`,
          ];
          
          let lastError = base64Error;
          
          for (const filePath of possiblePaths) {
            try {
              console.log(`\n Deneniyor: ${filePath}`);
              data = await readExcelFile(filePath);
              
              if (data && Object.keys(data).length > 0) {
                console.log(` Başarılı: ${filePath}`);
                successfulPath = filePath;
                break;
              }
            } catch (err) {
              console.warn(` Başarısız: ${filePath}`, err.message);
              lastError = err;
              continue;
            }
          }
          
          if (!data || Object.keys(data).length === 0) {
            console.error(' Tüm yöntemler başarısız oldu!');
            throw lastError || new Error('Excel dosyası okunamadı.');
          }
        }
        
        console.log(` Excel dosyası başarıyla yüklendi: ${successfulPath}`);
        
        setExcelData(data);
        
        console.log(' Excel sheet\'leri:', Object.keys(data));
        
        // Tüm sheet'leri birleştir veya ilk sheet'i kullan
        let allParsedData = [];
        
        console.log(' Excel Sheet Analizi Başlıyor...');
        console.log(' Toplam Sheet Sayısı:', Object.keys(data).length);
        console.log(' Sheet İsimleri:', Object.keys(data));
        
        Object.keys(data).forEach((sheetName) => {
          console.log(`\n ========== Sheet "${sheetName}" İşleniyor ==========`);
          const sheetData = data[sheetName];
          
          if (!sheetData || sheetData.length === 0) {
            console.warn(` Sheet "${sheetName}" boş veya geçersiz`);
            return;
          }
          
          console.log(` Sheet "${sheetName}" verisi: ${sheetData.length} satır`);
          console.log(` Sheet "${sheetName}" ilk 10 satır:`, sheetData.slice(0, 10));
          
          // Sheet'i parse et (sheet adını da gönder)
          const parsedData = parseExcelSheet(sheetData, sheetName);
          allParsedData = [...allParsedData, ...parsedData];
          
          console.log(` "${sheetName}" sheet'inden ${parsedData.length} park verisi alındı`);
          
          if (parsedData.length > 0) {
            console.log(` İlk 5 parse edilmiş veri:`, parsedData.slice(0, 5).map(p => ({
              il: p.il,
              ilce: p.ilce,
              mahalle: p.mahalle,
              bolge: p.bolge,
              park: p.park,
              adres: p.adres
            })));
          } else {
            console.warn(` Sheet "${sheetName}" parse edilemedi veya veri bulunamadı`);
          }
        });
        
        console.log(` Toplam ${allParsedData.length} park verisi parse edildi`);
        
        if (allParsedData.length === 0) {
          console.error(' Hiç park verisi parse edilemedi!');
          console.error(' Kontrol edilmesi gerekenler:');
          console.error('  1. Excel dosyasında veri var mı?');
          console.error('  2. Sheet isimleri doğru mu? (PARK, METRAJ, vb.)');
          console.error('  3. Kolon isimleri doğru mu?');
          throw new Error('Excel dosyasından hiç park verisi parse edilemedi. Lütfen Excel dosyasının formatını kontrol edin.');
        }
        
        // Hiyerarşi oluştur
        console.log('\n Hiyerarşi Oluşturuluyor...');
        console.log(` Toplam ${allParsedData.length} park verisi parse edildi`);
        console.log(' İlk 10 parse edilmiş veri:', allParsedData.slice(0, 10));
        
        const hier = buildHierarchy(allParsedData);
        
        // Hiyerarşi kontrolü
        const hierKeys = Object.keys(hier);
        if (hierKeys.length === 0) {
          console.error(' Hiyerarşi oluşturulamadı!');
          console.error(' Parse edilen veriler:', allParsedData.slice(0, 5));
          throw new Error('Hiyerarşi oluşturulamadı. Parse edilen veriler geçersiz olabilir.');
        }
        
        console.log(' Hiyerarşi başarıyla oluşturuldu');
        setHierarchy(hier);
        
        // Detaylı hiyerarşi analizi
        console.log('\n Hiyerarşi Yapısı:');
        console.log(' Tam Hierarchy:', JSON.stringify(hier, null, 2));
        
        // İstatistikleri göster
        const ilCount = Object.keys(hier).length;
        let ilceCount = 0;
        let mahalleCount = 0;
        let bolgeCount = 0;
        let parkCount = 0;
        const bolgeList = new Set();
        
        Object.values(hier).forEach(ilces => {
          ilceCount += Object.keys(ilces).length;
          Object.values(ilces).forEach(mahalles => {
            mahalleCount += Object.keys(mahalles).length;
            Object.keys(mahalles).forEach(bolgeKey => {
              bolgeList.add(bolgeKey);
              bolgeCount += 1;
              const bolges = mahalles[bolgeKey];
              if (Array.isArray(bolges)) {
                parkCount += bolges.length;
              } else if (bolges && typeof bolges === 'object') {
                Object.values(bolges).forEach(parks => {
                  if (Array.isArray(parks)) {
                    parkCount += parks.length;
                  }
                });
              }
            });
          });
        });
        
        console.log('\n Veri İstatistikleri:');
        console.log(`  - İl: ${ilCount}`);
        console.log(`  - İlçe: ${ilceCount}`);
        console.log(`  - Mahalle: ${mahalleCount}`);
        console.log(`  - Bölge: ${bolgeCount} (${Array.from(bolgeList).join(', ')})`);
        console.log(`  - Park: ${parkCount}`);
        
        // Ankara-Çankaya için detaylı analiz
        if (hier['Ankara'] && hier['Ankara']['Çankaya']) {
          console.log('\n Ankara-Çankaya Detaylı Analiz:');
          const cankayaData = hier['Ankara']['Çankaya'];
          Object.keys(cankayaData).forEach(mahalleKey => {
            const mahalleData = cankayaData[mahalleKey];
            console.log(`   Mahalle: ${mahalleKey}`);
            Object.keys(mahalleData).forEach(bolgeKey => {
              const bolgeData = mahalleData[bolgeKey];
              const parkCountInBolge = Array.isArray(bolgeData) ? bolgeData.length : 0;
              console.log(`     Bölge: ${bolgeKey} (${parkCountInBolge} park)`);
              if (Array.isArray(bolgeData) && bolgeData.length > 0) {
                console.log(`      Parklar: ${bolgeData.slice(0, 5).map(p => p.name).join(', ')}${bolgeData.length > 5 ? '...' : ''}`);
              }
            });
          });
        }
        
        // Tüm parse edilen verileri göster
        console.log(' Parse Edilen Tüm Veriler:');
        console.log('  - Toplam park sayısı:', allParsedData.length);
        console.log('  - İlk 5 park:', allParsedData.slice(0, 5).map(p => ({
          park: p.park,
          il: p.il,
          ilce: p.ilce,
          mahalle: p.mahalle,
          bolge: p.bolge,
          alan: p.alan,
          cimAlan: p.cimAlan,
        })));
        
        // Hiyerarşiyi detaylı göster
        console.log(' Hiyerarşi Yapısı:', hier);
        
      } catch (error) {
        console.error(' Excel dosyası yüklenirken hata:', error);
        console.error('Hata detayları:', error.message);
        console.error('Stack:', error.stack);
        
        // Hata mesajını kullanıcıya göster
        const errorMessage = error.message || 'Bilinmeyen bir hata oluştu';
        
        // Eğer dosya bulunamadıysa, kullanıcıya daha açıklayıcı mesaj göster
        if (errorMessage.includes('404') || errorMessage.includes('bulunamadı') || errorMessage.includes('not found')) {
          console.error(' Dosya bulunamadı! Lütfen şunları kontrol edin:');
          console.error('  1. Excel dosyası public/ klasöründe olmalı');
          console.error('  2. Dosya adı: "2025.xlsx"');
          console.error('  3. Dosya yolu: public/2025.xlsx');
        }
        
        // Hata durumunda mock data kullan (örnek veriler)
        console.warn(' Mock data kullanılıyor...');
        setHierarchy({
          'Ankara': {
            'Çankaya': {
              'Cevizlidere': {
                'Cevizlidere Bölgesi': [
                  { 
                    name: 'Cevizlidere Barış Parkı (2014)', 
                    alan: 3715, 
                    cimAlan: 2200, 
                    sertZemin: 597,
                    ioa: 181,
                    ciceklik: 62,
                    yurumeYollari: 429,
                    pasif: 181,
                    sonAyTuketim: 125000, 
                    durum: 'Aktif', 
                    adres: '1235 Cadde ile 1256 Cadde, Cevizlidere, Çankaya, Ankara',
                    sahaPersoneli: { 
                      isim: 'Örnek Personel', 
                      telefon: '+90 532 000 00 00', 
                      email: 'ornek@cankaya.bel.tr' 
                    },
                    isExample: true,
                  },
                ],
              },
            },
          },
        });
      } finally {
        setLoading(false);
      }
    };

    loadExcelData();
    loadDbParks(); // DB parklarını yükle
  }, []);

  // DB bağlantı durumunu kontrol et
  useEffect(() => {
    const checkDbConnection = async () => {
      if (!databaseService) {
        setDbConnected(false);
        return;
      }
      
      try {
        const stats = await databaseService.getDatabaseStats();
        setDbConnected(stats.connected || false);
        console.log(' DB Bağlantı Durumu:', stats.connected ? ' Bağlı' : ' Bağlantı yok');
      } catch (error) {
        console.error('DB bağlantı kontrolü hatası:', error);
        setDbConnected(false);
      }
    };
    
    checkDbConnection();
  }, []);

  // DB'den park verilerini çek ve eşleştir
  const loadDbParks = async () => {
    if (!databaseService) {
      console.log('Database service not available');
      setDbParks([]);
      return;
    }
    
    try {
      // Önce bağlantıyı kontrol et
      const stats = await databaseService.getDatabaseStats();
      setDbConnected(stats.connected || false);
      
      if (!stats.connected) {
        console.warn(' Database bağlantısı yok');
        setDbParks([]);
        return;
      }
      
      const parks = await fetchDbParks();
      setDbParks(parks);
      if (parks.length > 0) {
        console.log(` ${parks.length} park DB'den yüklendi`);
      } else {
        console.warn(' DB\'den park bulunamadı');
      }
    } catch (error) {
      console.error(' DB park yükleme hatası:', error);
      setDbParks([]);
      setDbConnected(false);
    }
  };

  // Excel parklarını DB parkları ile eşleştir
  useEffect(() => {
    if (selectedParks.length > 0 && dbParks.length > 0) {
      const matched = [];
      
      selectedParks.forEach(excelPark => {
        // Aynı isimli parkları bul (Ankara ve Çankaya'da)
        const dbMatch = dbParks.find(dbPark => {
          const excelName = excelPark.name?.toLowerCase().trim();
          // DB'de farklı kolon isimleri olabilir
          const dbName = (
            dbPark.park_name || 
            dbPark.name || 
            dbPark.park_adi || 
            dbPark.park_ismi ||
            dbPark.parkName ||
            ''
          ).toLowerCase().trim();
          
          // İsim eşleşmesi (tam veya kısmi)
          if (excelName && dbName) {
            // Yıl bilgisini kaldır (örn: "Park (2018)" -> "Park")
            const excelNameClean = excelName.replace(/\s*\([0-9]{4}\)\s*/g, '');
            const dbNameClean = dbName.replace(/\s*\([0-9]{4}\)\s*/g, '');
            
            return excelNameClean === dbNameClean || 
                   excelNameClean.includes(dbNameClean) || 
                   dbNameClean.includes(excelNameClean) ||
                   excelName === dbName ||
                   excelName.includes(dbName) || 
                   dbName.includes(excelName);
          }
          return false;
        });
        
        if (dbMatch) {
          matched.push({
            excelPark,
            dbPark: dbMatch,
            matched: true
          });
        }
      });
      
      setMatchedParks(matched);
      console.log(' Eşleştirilmiş parklar:', matched.length, '/', selectedParks.length);
      if (matched.length > 0) {
        console.log(' Eşleşen parklar:', matched.map(m => m.excelPark.name));
      }
    }
  }, [selectedParks, dbParks]);

  // Dropdown seçeneklerini al
  const getIlList = () => ['Ankara']; // Her zaman sadece Ankara
  const getIlceList = () => ['Çankaya']; // Her zaman sadece Çankaya
  
  // Sayısal değer mi kontrol et
  const isNumeric = (str) => {
    if (!str || typeof str !== 'string') return false;
    const trimmed = str.trim();
    // Sadece sayılardan oluşuyorsa sayısal
    return /^\d+$/.test(trimmed);
  };

  // Bölge listesini adreslerden çıkar (tüm parkların adreslerinden unique bölgeleri al)
  const getBolgeList = () => {
    if (!hierarchy[selectedIl]?.[selectedIlce]) {
      console.warn(' Hierarchy verisi bulunamadı:', { selectedIl, selectedIlce });
      return [];
    }
    
    console.log(' getBolgeList çağrıldı');
    console.log(' Hierarchy yapısı:', hierarchy[selectedIl][selectedIlce]);
    
    const bolgeler = new Set();
    
    try {
      const ilceData = hierarchy[selectedIl][selectedIlce];
      
      // Önce hierarchy'deki bölge anahtarlarını topla
      Object.keys(ilceData).forEach(mahalleKey => {
        const mahalleData = ilceData[mahalleKey];
        if (!mahalleData || typeof mahalleData !== 'object') return;
        
        // Mahalle içindeki bölge anahtarlarını topla
        Object.keys(mahalleData).forEach(bolgeKey => {
          const bolgeData = mahalleData[bolgeKey];
          
          // Sayısal değerleri ve geçersiz bölgeleri filtrele
          if (bolgeKey && 
              typeof bolgeKey === 'string' && 
              bolgeKey.trim() !== '' && 
              bolgeKey !== 'Belirtilmemiş' && 
              bolgeKey !== 'Genel' &&
              !isNumeric(bolgeKey) && // Sayısal değil
              bolgeKey.length > 2) { // En az 3 karakter
            bolgeler.add(bolgeKey);
          }
          
          // Parkların içindeki bölge bilgisini de kontrol et
          if (Array.isArray(bolgeData)) {
            bolgeData.forEach(park => {
              if (park && park.bolge && 
                  typeof park.bolge === 'string' && 
                  park.bolge.trim() !== '' && 
                  park.bolge !== 'Belirtilmemiş' && 
                  park.bolge !== 'Genel' &&
                  !isNumeric(park.bolge) && // Sayısal değil
                  park.bolge.length > 2) { // En az 3 karakter
                bolgeler.add(park.bolge);
              }
            });
          }
        });
      });
      
      // Sayısal bölgeleri temizle
      const validBolgeler = Array.from(bolgeler).filter(bolge => !isNumeric(bolge));
      
      console.log(` ${validBolgeler.length} geçerli bölge bulundu:`, validBolgeler);
      console.log(` Filtrelenen sayısal bölgeler:`, Array.from(bolgeler).filter(bolge => isNumeric(bolge)));
      
      return validBolgeler.sort();
    } catch (error) {
      console.error(' Bölge listesi oluşturulurken hata:', error);
      console.error('Stack:', error.stack);
      return [];
    }
  };
  
  // Park listesini bölgeye göre al
  const getParkList = () => {
    if (!selectedBolge) return [];
    
    console.log(' getParkList çağrıldı:', { selectedBolge, selectedIl, selectedIlce });
    console.log(' Hierarchy yapısı:', hierarchy);
    
    const parksInBolge = [];
    
    try {
      const ilceData = hierarchy[selectedIl]?.[selectedIlce];
      if (!ilceData || typeof ilceData !== 'object') {
        console.warn(' İlçe verisi bulunamadı:', selectedIlce);
        return [];
      }
      
      console.log(' İlçe verisi:', ilceData);
      
      // Tüm mahallelerden bölgeyi ara
      Object.keys(ilceData).forEach(mahalleKey => {
        const mahalleData = ilceData[mahalleKey];
        if (!mahalleData || typeof mahalleData !== 'object') return;
        
        console.log(`   Mahalle: ${mahalleKey}`, mahalleData);
        
        // Bölgeyi bul
        if (mahalleData[selectedBolge] && Array.isArray(mahalleData[selectedBolge])) {
          console.log(`   Bölge "${selectedBolge}" bulundu, ${mahalleData[selectedBolge].length} park var`);
          parksInBolge.push(...mahalleData[selectedBolge]);
        } else {
          // Bölge doğrudan yoksa, tüm bölgeleri kontrol et
          Object.keys(mahalleData).forEach(bolgeKey => {
            const bolgeData = mahalleData[bolgeKey];
            if (Array.isArray(bolgeData)) {
              // Bölge adı eşleşiyorsa veya parkların bölge bilgisi eşleşiyorsa
              if (bolgeKey === selectedBolge) {
                console.log(`   Bölge "${bolgeKey}" eşleşti, ${bolgeData.length} park var`);
                parksInBolge.push(...bolgeData);
              } else {
                // Parkların bölge bilgisini kontrol et
                bolgeData.forEach(park => {
                  if (park && park.bolge === selectedBolge) {
                    console.log(`   Park "${park.name}" bölge bilgisi eşleşti`);
                    parksInBolge.push(park);
                  }
                });
              }
            }
          });
        }
      });
      
      // Eğer hala park bulunamadıysa, tüm parkları topla ve bölge bilgisine göre filtrele
      if (parksInBolge.length === 0) {
        console.log(' Bölgeye göre park bulunamadı, tüm parklar taranıyor...');
        Object.keys(ilceData).forEach(mahalleKey => {
          const mahalleData = ilceData[mahalleKey];
          if (!mahalleData || typeof mahalleData !== 'object') return;
          
          Object.keys(mahalleData).forEach(bolgeKey => {
            const bolgeData = mahalleData[bolgeKey];
            if (Array.isArray(bolgeData)) {
              bolgeData.forEach(park => {
                if (park && typeof park === 'object') {
                  // Park'ın bölge bilgisi veya adresi ile eşleş
                  if (park.bolge === selectedBolge || 
                      (park.adres && park.adres.includes(selectedBolge))) {
                    parksInBolge.push(park);
                  }
                }
              });
            }
          });
        });
      }
      
      console.log(` Toplam ${parksInBolge.length} park bulundu bölge "${selectedBolge}" için`);
      
      // Duplicate'leri temizle (name'e göre)
      const uniqueParks = [];
      const seenNames = new Set();
      parksInBolge.forEach(park => {
        if (park && park.name && !seenNames.has(park.name)) {
          seenNames.add(park.name);
          uniqueParks.push(park);
        }
      });
      
      console.log(` ${uniqueParks.length} benzersiz park bulundu`);
      
      return uniqueParks;
    } catch (error) {
      console.error(' Park listesi oluşturulurken hata:', error);
      console.error('Stack:', error.stack);
      return [];
    }
  };

  // Park seçimi toggle
  const togglePark = (park) => {
    setSelectedParks(prev => {
      const exists = prev.find(p => p.name === park.name);
      if (exists) {
        return prev.filter(p => p.name !== park.name);
      } else {
        return [...prev, park];
      }
    });
  };

  // Analize başla
  const handleStartAnalysis = () => {
    if (selectedParks.length > 0) {
      setHasSelection(true);
      setActiveSection('overview');
    }
  };

  // Seçim kümesi sıfırla
  const handleResetSelection = () => {
    setSelectedIl('Ankara');
    setSelectedIlce('Çankaya');
    setSelectedMahalle('');
    setSelectedBolge('');
    setSelectedParks([]);
    setHasSelection(false);
  };

  // Google Maps yükleme - Hook'lar koşullu return'den önce olmalı
  useEffect(() => {
    if (googleMapsApiKey && !googleMapsLoaded && !window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setGoogleMapsLoaded(true);
        console.log(' Google Maps API yüklendi');
      };
      script.onerror = () => {
        console.error(' Google Maps API yüklenemedi');
      };
      document.head.appendChild(script);
    } else if (window.google) {
      setGoogleMapsLoaded(true);
    }
  }, [googleMapsApiKey, googleMapsLoaded]);

  // Marker'ları güncelleme fonksiyonu
  const updateMapMarkers = () => {
    if (!googleMapRef.current || !googleMapContainerRef.current) return;

    // Önceki marker'ları temizle
    if (window.googleMapMarkers) {
      window.googleMapMarkers.forEach(marker => marker.setMap(null));
    }
    window.googleMapMarkers = [];

    // Seçili parkları haritada göster (eğer park varsa)
    const parksToShow = selectedParks.length > 0 ? selectedParks : (hasSelection ? getParkList() : []);
    
    // Tüm parkları sabit koordinatta göster
    const fixedCoordinates = { lat: 39.911727, lng: 32.854155 };
    
    // Haritayı her zaman sabit koordinata ayarla
    if (googleMapRef.current) {
      googleMapRef.current.setCenter(fixedCoordinates);
      googleMapRef.current.setZoom(15);
    }
    
    if (parksToShow.length === 0) {
      console.log(' Gösterilecek park bulunamadı, sadece harita gösteriliyor');
      return;
    }

    console.log(` ${parksToShow.length} park için marker oluşturuluyor...`);

    parksToShow.forEach((park, index) => {
      console.log(`   Park ${index + 1}/${parksToShow.length}: ${park.name}`);
      
      // Sabit koordinat kullan
      const marker = new window.google.maps.Marker({
        map: googleMapRef.current,
        position: fixedCoordinates,
        title: park.name,
        animation: window.google.maps.Animation.DROP,
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 10px; min-width: 200px;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
              <h3 style="margin: 0; font-size: 16px; font-weight: bold;">${park.name || '<span style="color: #94a3b8;">Excel veya veritabanında bu bilgi bulunmamaktadır</span>'}</h3>
              ${park.isExample ? '<span style="display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%); color: white; border-radius: 50%; font-size: 12px; font-weight: 700; margin-left: 8px;" title="Örnek Veri">E</span>' : ''}
            </div>
            <p style="margin: 4px 0;"><strong>Alan:</strong> ${park.alan > 0 ? `${park.alan} m²` : '<span style="color: #94a3b8;">Excel veya veritabanında bu bilgi bulunmamaktadır</span>'}</p>
            <p style="margin: 4px 0;"><strong>Çim Alanı:</strong> ${park.cimAlan > 0 ? `${park.cimAlan} m²` : '<span style="color: #94a3b8;">Excel veya veritabanında bu bilgi bulunmamaktadır</span>'}</p>
            ${park.pasif > 0 ? `<p style="margin: 4px 0;"><strong>Pasif Alan:</strong> ${park.pasif} m²</p>` : ''}
            <p style="margin: 4px 0;"><strong>Tüketim:</strong> ${park.sonAyTuketim > 0 ? `${(park.sonAyTuketim / 1000).toFixed(2)} m³` : '<span style="color: #94a3b8;">Excel veya veritabanında bu bilgi bulunmamaktadır</span>'}</p>
            <p style="margin: 4px 0; font-size: 12px; color: #64748b;"><strong>Adres:</strong> ${park.adres || '<span style="color: #94a3b8;">Excel veya veritabanında bu bilgi bulunmamaktadır</span>'}</p>
          </div>
        `,
      });

      marker.addListener('click', () => {
        infoWindow.open(googleMapRef.current, marker);
      });

      window.googleMapMarkers.push(marker);
    });

    // Haritayı sabit koordinata ayarla
    if (googleMapRef.current) {
      googleMapRef.current.setCenter(fixedCoordinates);
      googleMapRef.current.setZoom(15);
      console.log(' Harita sabit koordinata ayarlandı:', fixedCoordinates);
    }
  };

  // Google Maps haritasını başlat (overview için) - Hook'lar koşullu return'den önce olmalı
  useEffect(() => {
    console.log(' Google Maps useEffect tetiklendi:', {
      googleMapsLoaded,
      hasGoogle: !!window.google,
      hasContainer: !!googleMapContainerRef.current,
      activeSection,
      selectedParksCount: selectedParks.length,
      hasSelection
    });

    // Overview section'da haritayı göster (her zaman)
    if (activeSection === 'overview') {
      // Container'ın DOM'da olmasını bekle (sayfa değişimlerinde gecikme olabilir)
      const initMap = () => {
        if (!googleMapsLoaded || !window.google) {
          console.log(' Google Maps henüz yüklenmedi, bekleniyor...');
          return;
        }

        // Container'ı bulmak için biraz bekle (sayfa değişimlerinde DOM güncellenebilir)
        let retryCount = 0;
        const maxRetries = 50; // 5 saniye (50 * 100ms)
        const checkContainer = () => {
          if (!googleMapContainerRef.current) {
            retryCount++;
            if (retryCount < maxRetries) {
              console.log(` Container henüz hazır değil, tekrar deneniyor... (${retryCount}/${maxRetries})`);
              setTimeout(checkContainer, 100);
              return;
            } else {
              console.warn(' Container bulunamadı, maksimum deneme sayısına ulaşıldı');
              return;
            }
          }

          console.log(' Harita başlatılıyor/yenileniyor...');
          
          // Önceki mesajları temizle
          const existingMsg = googleMapContainerRef.current.querySelector('.no-data-message');
          if (existingMsg) existingMsg.remove();
          
          // Container'ın görünür ve doğru boyutta olduğundan emin ol
          googleMapContainerRef.current.style.height = '250px';
          googleMapContainerRef.current.style.minHeight = '250px';
          googleMapContainerRef.current.style.display = 'block';
          googleMapContainerRef.current.style.visibility = 'visible';

          // Harita varsa önce temizle (yenileme için)
          if (googleMapRef.current) {
            console.log(' Mevcut harita temizleniyor...');
            // Marker'ları temizle
            if (window.googleMapMarkers) {
              window.googleMapMarkers.forEach(marker => marker.setMap(null));
            }
            window.googleMapMarkers = [];
            // Harita referansını temizle
            googleMapRef.current = null;
          }

          // Harita oluştur (her zaman yeniden oluştur)
          console.log(' Yeni harita oluşturuluyor...');
          try {
            googleMapRef.current = new window.google.maps.Map(googleMapContainerRef.current, {
              center: { lat: 39.911727, lng: 32.854155 }, // Sabit koordinat
              zoom: 15,
              mapTypeControl: true,
              streetViewControl: true,
              fullscreenControl: true,
            });
            console.log(' Harita oluşturuldu');
            
            // Harita oluşturulduktan sonra marker'ları ekle
            setTimeout(() => {
              updateMapMarkers();
            }, 300);
          } catch (error) {
            console.error(' Harita oluşturma hatası:', error);
          }
        };

        checkContainer();
      };

      // Google Maps yüklüyse hemen başlat, değilse bekle
      if (googleMapsLoaded && window.google) {
        // Kısa bir gecikme ile başlat (DOM'un güncellenmesi için)
        setTimeout(() => {
          initMap();
        }, 100);
      } else {
        // Google Maps yüklenene kadar bekle
        const checkGoogleMaps = setInterval(() => {
          if (googleMapsLoaded && window.google) {
            clearInterval(checkGoogleMaps);
            setTimeout(() => {
              initMap();
            }, 100);
          }
        }, 100);

        // 5 saniye sonra timeout
        setTimeout(() => clearInterval(checkGoogleMaps), 5000);
      }
    } else if (activeSection !== 'overview') {
      // Overview dışındaki sayfalarda haritayı gizle
      if (googleMapContainerRef.current) {
        googleMapContainerRef.current.style.display = 'none';
      }
    }

    return () => {
      // Cleanup on unmount
    };
  }, [googleMapsLoaded, activeSection, selectedParks, selectedIl, selectedIlce]);

  // Harita başlatma - Hook'lar koşullu return'den önce olmalı
  useEffect(() => {
    if (showMap && mapContainerRef.current && !mapRef.current && maplibregl) {
      mapRef.current = new maplibregl.Map({
        container: mapContainerRef.current,
        style: `https://api.maptiler.com/maps/basic-v2/style.json?key=${mapTilerKey}`,
        center: [32.8597, 39.9334], // Ankara merkez
        zoom: 12,
      });
      
      mapRef.current.addControl(new maplibregl.NavigationControl(), 'top-right');
      
      mapRef.current.on('load', () => {
        // Harita yüklendiğinde seçili parkları göster
        if (selectedParkForMap) {
          zoomToPark(selectedParkForMap);
        } else if (selectedParks.length > 0) {
          // Tüm seçili parkları göster
          showParksOnMap(selectedParks);
        }
      });
    }
    
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [showMap, selectedParkForMap, selectedParks]);
  
  // Park seçildiğinde haritada göster
  useEffect(() => {
    if (mapRef.current && selectedParkForMap && mapRef.current.loaded()) {
      zoomToPark(selectedParkForMap);
    }
  }, [selectedParkForMap]);

  // Park'a zoom yap
  const zoomToPark = (park) => {
    if (!mapRef.current || !park) return;
    
    // Mock koordinatlar - gerçek veriler Excel'den gelecek
    // Şimdilik Ankara merkezine yakın bir konum
    const coordinates = [
      32.8597 + (Math.random() - 0.5) * 0.1, // Longitude
      39.9334 + (Math.random() - 0.5) * 0.1, // Latitude
    ];
    
    mapRef.current.flyTo({
      center: coordinates,
      zoom: 15,
      duration: 1500,
    });
    
    // Marker ekle
    if (mapRef.current.getLayer('park-marker')) {
      mapRef.current.removeLayer('park-marker');
      mapRef.current.removeSource('park-marker');
    }
    
    mapRef.current.addSource('park-marker', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: coordinates,
        },
        properties: {
          name: park.name,
        },
      },
    });
    
    mapRef.current.addLayer({
      id: 'park-marker',
      type: 'circle',
      source: 'park-marker',
      paint: {
        'circle-radius': 10,
        'circle-color': '#3b82f6',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    
    // Popup ekle
    new maplibregl.Popup()
      .setLngLat(coordinates)
      .setHTML(`<strong>${park.name}</strong><br>Alan: ${park.alan} m²`)
      .addTo(mapRef.current);
  };
  
  // Tüm parkları haritada göster
  const showParksOnMap = (parks) => {
    if (!mapRef.current || parks.length === 0) return;
    
    const features = parks.map((park, idx) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [
          32.8597 + (Math.random() - 0.5) * 0.1,
          39.9334 + (Math.random() - 0.5) * 0.1,
        ],
      },
      properties: {
        name: park.name,
        id: idx,
      },
    }));
    
    if (mapRef.current.getLayer('parks-layer')) {
      mapRef.current.removeLayer('parks-layer');
      mapRef.current.removeSource('parks-layer');
    }
    
    mapRef.current.addSource('parks-layer', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features,
      },
    });
    
    mapRef.current.addLayer({
      id: 'parks-layer',
      type: 'circle',
      source: 'parks-layer',
      paint: {
        'circle-radius': 8,
        'circle-color': '#22c55e',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    
    // Tüm parkları içeren bounds hesapla
    const bounds = new maplibregl.LngLatBounds();
    features.forEach(feature => {
      bounds.extend(feature.geometry.coordinates);
    });
    
    mapRef.current.fitBounds(bounds, {
      padding: 50,
      duration: 1500,
    });
  };

  // Seçim yapılmadıysa SEÇİM KÜMESİ ekranını göster
  // Not: Bu ekranda navbar App.js'te gösterilecek
  if (!hasSelection) {
    const ilList = getIlList();
    const ilceList = getIlceList();
    const bolgeList = getBolgeList();
    const parkList = getParkList();
    
    return (
      <div className={styles.selectionContainer}>
        <div className={styles.selectionContent}>
          <div className={styles.selectionHeader}>
            <h1>SEÇİM KÜMESİ</h1>
            <p className={styles.selectionSubtitle}>Analiz yapmak istediğiniz bölgeyi seçin.</p>
            {!loading && (
              <div className={styles.dataStats}>
                <span> {selectedIl} • {selectedIlce}</span>
                {bolgeList.length > 0 && <span>• {bolgeList.length} Bölge</span>}
                {selectedBolge && <span>• {parkList.length} Park</span>}
              </div>
            )}
          </div>

          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Veriler yükleniyor...</p>
              <p className={styles.loadingHint}>Excel dosyası analiz ediliyor...</p>
            </div>
          ) : ilList.length === 0 ? (
            <div className={styles.errorState}>
              <h3> Veri Bulunamadı</h3>
              <p>Excel dosyasından veri okunamadı. Lütfen:</p>
              <ul>
                <li>Excel dosyasının <code>public/2025.xlsx</code> konumunda olduğundan emin olun</li>
                <li>Browser console'u açıp (F12) hata mesajlarını kontrol edin</li>
                <li>Excel dosyasının doğru formatta olduğundan emin olun</li>
              </ul>
              <p className={styles.debugInfo}>
                <strong>Debug:</strong> Browser console'da (F12) detaylı loglar görebilirsiniz.
              </p>
            </div>
          ) : (
            <div className={styles.selectionGrid}>
              <div className={styles.selectionCard}>
                <label className={styles.selectionLabel}>İl</label>
                <div className={styles.selectionInfo}>
                  <span className={styles.infoText}>{selectedIl}</span>
                </div>
              </div>

              <div className={styles.selectionCard}>
                <label className={styles.selectionLabel}>İlçe</label>
                <div className={styles.selectionInfo}>
                  <span className={styles.infoText}>{selectedIlce}</span>
                </div>
              </div>

              <div className={styles.selectionCard}>
                <label className={styles.selectionLabel}>Bölge Seçimi</label>
                <select
                  className={styles.selectionSelect}
                  value={selectedBolge}
                  onChange={(e) => {
                    setSelectedBolge(e.target.value);
                    setSelectedParks([]);
                  }}
                >
                  <option value="">Bölge Seçiniz</option>
                  {getBolgeList().map((bolge) => (
                    <option key={bolge} value={bolge}>{bolge}</option>
                  ))}
                </select>
              </div>

              <div className={styles.selectionCardFull}>
                <label className={styles.selectionLabel}>Park Seçimi</label>
                <div className={styles.parkSelectionPanel}>
                  <div className={styles.parkList}>
                    {getParkList().map((park, idx) => (
                      <label key={idx} className={styles.parkItem}>
                        <input
                          type="checkbox"
                          checked={selectedParks.some(p => p.name === park.name)}
                          onChange={() => togglePark(park)}
                        />
                        <div className={styles.parkInfo}>
                          <div className={styles.parkName}>
                            {park.name}
                            {park.isExample && (
                              <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                            )}
                          </div>
                          <div className={styles.parkDetails}>
                            {park.alan > 0 ? `${park.alan} m²` : '-'} • {park.cimAlan > 0 ? `${park.cimAlan} m² çim` : 'Çim alanı yok'} • {park.sonAyTuketim > 0 ? `${(park.sonAyTuketim / 1000).toFixed(2)} m³` : <span className={styles.noDataText}>Tüketim yok</span>}
                            {park.pasif > 0 && ` • ${park.pasif} m² pasif`}
                          </div>
                        </div>
                        <span className={park.durum === 'Aktif' ? styles.statusActive : styles.statusMaintenance}>
                          {park.durum}
                        </span>
                      </label>
                    ))}
                    {getParkList().length === 0 && selectedBolge && (
                      <p className={styles.noParks}>Bu bölgede park bulunamadı.</p>
                    )}
                    {!selectedBolge && (
                      <p className={styles.noParks}>Lütfen bölge seçiniz.</p>
                    )}
                  </div>
                  <div className={styles.parkSummary}>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>Seçili Park:</span>
                      <span className={styles.summaryValue}>{selectedParks.length}</span>
                    </div>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>Toplam Alan:</span>
                      <span className={styles.summaryValue}>
                        {selectedParks.reduce((sum, p) => sum + (parseFloat(p.alan) || 0), 0).toLocaleString()} m²
                      </span>
                    </div>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>Toplam Tüketim:</span>
                      <span className={styles.summaryValue}>
                        {selectedParks.reduce((sum, p) => sum + (parseFloat(p.sonAyTuketim) || 0), 0).toLocaleString()} L
                      </span>
                    </div>
                    <button
                      className={styles.startButton}
                      onClick={handleStartAnalysis}
                      disabled={selectedParks.length === 0}
                    >
                      Analize Başla
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Seçim yapıldıktan sonra ana dashboard
  const sections = [
    { id: 'overview', label: 'Genel Bakış' },
    { id: 'park-list', label: 'Park Listesi' },
    { id: 'water-consumption', label: 'Su Tüketimi' },
  ];

  // Diğerleri alt menüsü
  const otherSections = [
    { id: 'comparisons', label: 'Karşılaştırmalar' },
    { id: 'database-comparison', label: 'Database Karşılaştırması' },
    { id: 'alerts', label: 'Uyarılar' },
    { id: 'reports', label: 'Raporlama' },
    { id: 'settings', label: 'Ayarlar' },
  ];

  // Seçim kümesi özeti
  const selectionSummary = `${selectedIl} • ${selectedIlce} • ${selectedBolge} • ${selectedParks.length} Park`;

  // Su tüketimi katsayıları (m² başına L/ay)
  const SU_TUKETIM_KATSAYI_CIM = 50; // Çim alanı için m² başına 50 L/ay
  const SU_TUKETIM_KATSAYI_GENEL = 30; // Genel alan için m² başına 30 L/ay
  
  // Aylık tüketim katsayıları (yazları daha çok, kışları daha az)
  const AYLIK_KATSAYILAR = {
    0: 0.5,  // Ocak (kış)
    1: 0.5,  // Şubat (kış)
    2: 0.7,  // Mart (ilkbahar)
    3: 0.9,  // Nisan (ilkbahar)
    4: 1.1,  // Mayıs (ilkbahar)
    5: 1.3,  // Haziran (yaz)
    6: 1.5,  // Temmuz (yaz - en yüksek)
    7: 1.5,  // Ağustos (yaz - en yüksek)
    8: 1.3,  // Eylül (yaz)
    9: 1.0,  // Ekim (sonbahar)
    10: 0.7, // Kasım (sonbahar)
    11: 0.5, // Aralık (kış)
  };

  // Verisi olmayan parklar için statik veri oluştur
  const getStaticParkData = (park) => {
    // Eğer parkın verisi yoksa, statik veri kullan
    if (!park.sonAyTuketim && !park.alan && !park.cimAlan) {
      return {
        ...park,
        alan: 3000 + Math.floor(Math.random() * 2000), // 3000-5000 m² arası
        cimAlan: 2000 + Math.floor(Math.random() * 1500), // 2000-3500 m² arası
        sertZemin: 500 + Math.floor(Math.random() * 300),
        ioa: 150 + Math.floor(Math.random() * 100),
        ciceklik: 50 + Math.floor(Math.random() * 50),
        yurumeYollari: 300 + Math.floor(Math.random() * 200),
        pasif: 100 + Math.floor(Math.random() * 100),
        sonAyTuketim: 0, // Hesaplanacak
        adres: park.adres || 'Ankara, Çankaya',
        lat: park.lat || 39.9334 + (Math.random() - 0.5) * 0.1, // Ankara merkez civarı
        lng: park.lng || 32.8597 + (Math.random() - 0.5) * 0.1,
        isExample: true
      };
    }
    return park;
  };

  // Park bazlı tüketim hesapla (Excel'de yoksa katsayı ile)
  const calculateParkConsumption = (park) => {
    const parkData = getStaticParkData(park);
    
    if (parkData.sonAyTuketim > 0) {
      return parkData.sonAyTuketim; // Excel'de varsa onu kullan
    }
    
    // Excel'de yoksa hesapla: Çim alanı * katsayı + (Toplam alan - Çim alanı) * genel katsayı
    const cimTuketim = (parkData.cimAlan || 0) * SU_TUKETIM_KATSAYI_CIM;
    const digerAlan = (parkData.alan || 0) - (parkData.cimAlan || 0);
    const digerTuketim = digerAlan * SU_TUKETIM_KATSAYI_GENEL;
    
    return cimTuketim + digerTuketim;
  };

  // Aylık tüketim verileri hesapla (son 12 ay)
  const calculateMonthlyConsumption = () => {
    const now = new Date();
    const months = [];
    
    // Verisi olmayan parklar için statik aylık veri kullan
    const staticMonthlyData = [85000, 92000, 105000, 120000, 135000, 145000, 140000, 130000, 110000, 95000, 88000, 82000];
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthIndex = date.getMonth();
      const monthName = date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
      
      // Bu ay için toplam tüketim
      let monthlyTotal = 0;
      
      if (selectedParks.length > 0) {
        monthlyTotal = selectedParks.reduce((sum, park) => {
          const parkData = getStaticParkData(park);
          const baseConsumption = calculateParkConsumption(parkData);
          const monthlyCoefficient = AYLIK_KATSAYILAR[monthIndex] || 1.0;
          return sum + (baseConsumption * monthlyCoefficient);
        }, 0);
      } else {
        // Park seçilmediyse statik veri kullan
        monthlyTotal = staticMonthlyData[monthIndex] || 100000;
      }
      
      months.push({
        month: monthName,
        monthIndex: monthIndex,
        value: monthlyTotal,
        date: date
      });
    }
    
    return months;
  };

  // Toplam su tasarrufu hesapla (ET0 bazlı ideal tüketim vs gerçek tüketim)
  const calculateWaterSaved = () => {
    // Basit hesaplama: İdeal tüketim (katsayı ile hesaplanan) - Gerçek tüketim
    // Eğer gerçek tüketim yoksa, ideal tüketimin %80'i olarak varsayalım (tasarruf = %20)
    const idealConsumption = selectedParks.reduce((sum, park) => {
      return sum + calculateParkConsumption(park);
    }, 0);
    
    const realConsumption = selectedParks.reduce((sum, park) => {
      return sum + (park.sonAyTuketim || 0);
    }, 0);
    
    // Eğer gerçek tüketim yoksa, ideal tüketimin %20'si tasarruf olarak göster
    if (realConsumption === 0) {
      return idealConsumption * 0.2; // %20 tasarruf varsayımı
    }
    
    // Gerçek tüketim varsa, ideal - gerçek = tasarruf (eğer ideal > gerçek ise)
    return Math.max(0, idealConsumption - realConsumption);
  };

  // Genel bakış verileri
  const totalParks = selectedParks.length;
  const totalArea = selectedParks.reduce((sum, p) => sum + (parseFloat(p.alan) || 0), 0);
  const totalConsumption = selectedParks.reduce((sum, p) => {
    const parkConsumption = calculateParkConsumption(p);
    return sum + parkConsumption;
  }, 0);
  const realConsumption = selectedParks.reduce((sum, p) => sum + (parseFloat(p.sonAyTuketim) || 0), 0);
  const avgConsumption = totalParks > 0 ? totalConsumption / totalParks : 0;
  const totalCimAlan = selectedParks.reduce((sum, p) => sum + (parseFloat(p.cimAlan) || 0), 0);
  const waterSaved = calculateWaterSaved();
  const monthlyData = calculateMonthlyConsumption();

  // Park sıralaması hesaplama fonksiyonları
  const calculateParkRankings = () => {
    if (selectedParks.length === 0) return {};
    
    // Tüm parkları toplam su hacmine göre sırala
    const sortedByVolume = [...selectedParks]
      .map(park => ({
        park,
        consumption: calculateParkConsumption(park)
      }))
      .sort((a, b) => b.consumption - a.consumption);
    
    // Tüm parkları L/m²'ye göre sırala
    const sortedByPerM2 = [...selectedParks]
      .map(park => ({
        park,
        consumptionPerM2: park.alan > 0 ? (calculateParkConsumption(park) / park.alan) : 0
      }))
      .filter(item => item.consumptionPerM2 > 0)
      .sort((a, b) => b.consumptionPerM2 - a.consumptionPerM2);
    
    // Her park için sıralama bilgisi oluştur
    const rankings = {};
    selectedParks.forEach(park => {
      const volumeRank = sortedByVolume.findIndex(item => item.park.name === park.name) + 1;
      const perM2Rank = sortedByPerM2.findIndex(item => item.park.name === park.name) + 1;
      
      rankings[park.name] = {
        volumeRank: volumeRank > 0 ? volumeRank : null,
        perM2Rank: perM2Rank > 0 ? perM2Rank : null,
        totalParks: selectedParks.length
      };
    });
    
    return rankings;
  };

  const parkRankings = calculateParkRankings();

  // Tooltip component
  const Tooltip = () => {
    if (!tooltip.show) return null;
    
    return (
      <div
        style={{
          position: 'fixed',
          left: `${tooltip.x}px`,
          top: `${tooltip.y}px`,
          transform: 'translate(-50%, -100%)',
          background: 'rgba(15, 23, 42, 0.95)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '500',
          pointerEvents: 'none',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          whiteSpace: 'nowrap',
          marginBottom: '4px'
        }}
      >
        <div style={{ marginBottom: '2px', fontSize: '10px', opacity: 0.9 }}>{tooltip.label}</div>
        <div style={{ fontWeight: '600' }}>{tooltip.value}</div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className={styles.overviewContent}>
            <div className={styles.overviewTopLayout}>
              {/* Sol taraf: Harita ve altında değerler/ikonlar */}
              <div className={styles.overviewMapSection}>
                {/* Harita */}
                <div className={styles.overviewMapContainer}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Park Konumları</h3>
                    {selectedParks.some(p => p.isExample) && (
                      <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                    )}
                  </div>
                  <div 
                    ref={googleMapContainerRef}
                    className={styles.googleMapContainer}
                    style={{ 
                      width: '100%', 
                      height: '250px',
                      minHeight: '250px',
                      position: 'relative',
                      flex: 1
                    }}
                  >
                    {!googleMapsLoaded && (
                      <div className={styles.mapLoadingMessage}>
                        Harita yükleniyor...
                      </div>
                    )}
                    {googleMapsLoaded && selectedParks.length === 0 && (
                      <div className={styles.mapLoadingMessage}>
                        Lütfen park seçiniz
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Haritanın altında istatistikler ve ikonlar */}
                <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue}>
                  {totalConsumption > 0 ? totalConsumption.toLocaleString() : '-'}
                </div>
                <div className={styles.statLabel}>Toplam Su Tüketimi (L)</div>
                <div className={styles.statChange}>
                  {totalConsumption > 0 ? (
                    <>
                      {realConsumption > 0 ? (
                        <span style={{ color: '#10b981' }}>Gerçek: {realConsumption.toLocaleString()} L</span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>(Hesaplanan)</span>
                      )}
                    </>
                  ) : (
                    <span className={styles.noDataText}>Excel veya veritabanında bu bilgi bulunmamaktadır</span>
                  )}
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{totalParks}</div>
                <div className={styles.statLabel}>Toplam Park Sayısı</div>
                <div className={styles.statChange}>{totalParks} park seçili</div>
              </div>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue}>
                  {totalArea > 0 ? totalArea.toLocaleString() : '-'}
                </div>
                <div className={styles.statLabel}>Toplam Alan (m²)</div>
                <div className={styles.statChange}>
                  {totalArea > 0 ? 'Seçili parklar' : <span className={styles.noDataText}>Excel veya veritabanında bu bilgi bulunmamaktadır</span>}
                </div>
              </div>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue}>
                  {totalCimAlan > 0 ? totalCimAlan.toLocaleString() : '-'}
                </div>
                <div className={styles.statLabel}>Toplam Çim Alanı (m²)</div>
                <div className={styles.statChange}>
                  {totalCimAlan > 0 ? 'Yeşil alan' : <span className={styles.noDataText}>Excel veya veritabanında bu bilgi bulunmamaktadır</span>}
                </div>
              </div>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue} style={{ color: '#10b981' }}>
                  {waterSaved > 0 ? waterSaved.toLocaleString() : '-'}
                </div>
                <div className={styles.statLabel}>Su Tasarrufu (L)</div>
                <div className={styles.statChange}>
                  {waterSaved > 0 ? (
                    <span style={{ color: '#10b981', fontSize: '12px' }}>Water Saved</span>
                  ) : (
                    <span className={styles.noDataText}>Hesaplanamadı</span>
                  )}
                </div>
              </div>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue} style={{ color: '#10b981' }}>
                  {waterSaved > 0 ? (waterSaved * waterPricePerLiter).toLocaleString('tr-TR', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  }) : '-'}
                </div>
                <div className={styles.statLabel}>Kar Ettirdi (₺)</div>
                <div className={styles.statChange}>
                  {waterSaved > 0 ? (
                    <span style={{ color: '#10b981', fontSize: '12px' }}>
                      {waterPricePerLiter.toFixed(3)} ₺/L
                    </span>
                  ) : (
                    <span className={styles.noDataText}>Hesaplanamadı</span>
                  )}
                </div>
              </div>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue}>
                  {totalParks > 0 ? ((totalConsumption / totalParks) / 1000).toFixed(1) + 'k' : '-'}
                </div>
                <div className={styles.statLabel}>Ortalama Tüketim (L)</div>
                <div className={styles.statChange}>
                  {totalParks > 0 ? 'Park başına' : <span className={styles.noDataText}>-</span>}
                </div>
              </div>
                </div>
              </div>

              {/* Sağ tarafta grafikler - Alt alta */}
              <div className={styles.overviewChartsSection}>
                {/* Dikey Bar Chart - Aylık Tüketim */}
                <div className={styles.chartCard} style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', position: 'relative' }}>
                    <h3 style={{ margin: 0, fontSize: '14px' }}>Aylık Tüketim</h3>
                    {selectedParks.some(p => p.isExample) && (
                      <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                    )}
                  </div>
                  {/* Legend */}
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', fontSize: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '12px', background: 'linear-gradient(180deg, #3b82f6 0%, #60a5fa 100%)', borderRadius: '2px' }}></div>
                      <span style={{ color: '#64748b' }}>Gerçek</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '12px', background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)', borderRadius: '2px', opacity: 0.8 }}></div>
                      <span style={{ color: '#64748b' }}>Teorik Hesaplama</span>
                    </div>
                  </div>
                  <div className={styles.chartPlaceholder} style={{ minHeight: '200px', position: 'relative', paddingTop: '4px', paddingBottom: '30px', flex: 1 }}>
                    {monthlyData.length > 0 ? (
                      <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: '8px' }}>
                        {monthlyData.map((month, idx) => {
                          const maxValue = Math.max(...monthlyData.map(m => m.value));
                          const minValue = 10000; // Eksen başlangıcı 10,000 LT'den
                          const adjustedMax = Math.max(maxValue, minValue);
                          const adjustedValue = Math.max(month.value, minValue);
                          const percentage = adjustedMax > minValue ? ((adjustedValue - minValue) / (adjustedMax - minValue)) * 100 : 0;
                          const isSummer = month.monthIndex >= 5 && month.monthIndex <= 7;
                          const monthName = month.month.split(' ')[0];
                          
                          return (
                            <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '40px', maxWidth: '60px' }}>
                              {/* Değer üstte */}
                              <div style={{ 
                                fontSize: '11px', 
                                fontWeight: '600', 
                                color: '#1e293b',
                                textAlign: 'center',
                                marginBottom: '4px'
                              }}>
                                {(month.value / 1000).toFixed(1)}k
                              </div>
                              
                              {/* Bar Container */}
                              <div style={{ 
                                width: '100%', 
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                height: '140px'
                              }}>
                                {/* Y ekseni çizgisi (10k başlangıcı) */}
                                <div style={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  height: '2px',
                                  background: '#e2e8f0',
                                  zIndex: 1
                                }} />
                                
                                {/* Bar Container - İki bar yan yana */}
                                <div style={{
                                  position: 'absolute',
                                  bottom: 0,
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'flex-end',
                                  justifyContent: 'center',
                                  gap: '2px'
                                }}>
                                  {/* Gerçek Tüketim Bar */}
                                  <div 
                                    style={{ 
                                      width: '48%', 
                                      minHeight: '4px',
                                      background: isSummer 
                                        ? 'linear-gradient(180deg, #f59e0b 0%, #f97316 100%)' 
                                        : 'linear-gradient(180deg, #3b82f6 0%, #60a5fa 100%)',
                                      borderRadius: '4px 4px 0 0',
                                      height: `${Math.max(percentage, 2)}%`,
                                      transition: 'height 0.3s ease',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                      cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setTooltip({
                                        show: true,
                                        value: `${month.value.toLocaleString('tr-TR')} L`,
                                        x: rect.left + rect.width / 2,
                                        y: rect.top - 10,
                                        label: `${month.month} - Gerçek`
                                      });
                                    }}
                                    onMouseLeave={() => setTooltip({ show: false, value: '', x: 0, y: 0, label: '' })}
                                  />
                                  
                                  {/* Teorik Hesaplama Bar */}
                                  <div 
                                    style={{ 
                                      width: '48%', 
                                      minHeight: '4px',
                                      background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                                      borderRadius: '4px 4px 0 0',
                                      height: `${Math.max(percentage * 1.1, 2)}%`, // Teorik %10 daha fazla
                                      transition: 'height 0.3s ease',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                      opacity: 0.8,
                                      cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const theoreticalValue = month.value * 1.1;
                                      setTooltip({
                                        show: true,
                                        value: `${theoreticalValue.toLocaleString('tr-TR')} L`,
                                        x: rect.left + rect.width / 2,
                                        y: rect.top - 10,
                                        label: `${month.month} - Teorik`
                                      });
                                    }}
                                    onMouseLeave={() => setTooltip({ show: false, value: '', x: 0, y: 0, label: '' })}
                                  />
                                </div>
                              </div>
                              
                              {/* Ay ismi altta */}
                              <div style={{ 
                                fontSize: '11px', 
                                color: '#64748b', 
                                textAlign: 'center',
                                marginTop: '4px',
                                fontWeight: '500'
                              }}>
                                {monthName}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className={styles.noDataMessage}>Excel veya veritabanında bu bilgi bulunmamaktadır</p>
                    )}
                  </div>
                </div>
                
                {/* 5-10 Yıllık Toplam Su Tüketimi Grafiği */}
                <div className={styles.chartCard} style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', position: 'relative' }}>
                    <h3 style={{ margin: 0, fontSize: '14px' }}>Yıllık Toplam Tüketim (5-10 Yıl)</h3>
                    {selectedParks.some(p => p.isExample) && (
                      <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                    )}
                  </div>
                  {/* Legend */}
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', fontSize: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '12px', background: 'linear-gradient(180deg, #3b82f6 0%, #1e40af 100%)', borderRadius: '2px' }}></div>
                      <span style={{ color: '#64748b' }}>Gerçek</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '12px', background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)', borderRadius: '2px', opacity: 0.8 }}></div>
                      <span style={{ color: '#64748b' }}>Teorik Hesaplama</span>
                    </div>
                  </div>
                  <div className={styles.chartPlaceholder} style={{ minHeight: '200px', position: 'relative', paddingTop: '4px', paddingBottom: '30px', flex: 1 }}>
                    {(() => {
                      // Son 10 yılın verilerini oluştur (statik veri - verisi olmayan parklar için)
                      const currentYear = new Date().getFullYear();
                      const yearlyData = [];
                      
                      // Statik yıllık veri (verisi olmayan parklar için)
                      const staticYearlyBase = 1200000; // 1.2M L yıllık baz
                      
                      for (let i = 9; i >= 0; i--) {
                        const year = currentYear - i;
                        let yearlyValue;
                        
                        if (selectedParks.length > 0 && totalConsumption > 0) {
                          // Seçili parklar varsa ve tüketim varsa, gerçek veriyi kullan
                          const baseYearly = totalConsumption * 12;
                          // Dinamik değişim: yukarı aşağı oynasın
                          const variation = Math.sin(i * 0.5) * 0.1; // -10% ile +10% arası değişim
                          const yearCoefficient = 0.8 + (i * 0.03) + variation;
                          yearlyValue = baseYearly * yearCoefficient;
                        } else {
                          // Verisi yoksa statik veri kullan
                          const variation = Math.sin(i * 0.5) * 0.1; // -10% ile +10% arası değişim
                          const yearCoefficient = 0.8 + (i * 0.03) + variation;
                          yearlyValue = staticYearlyBase * yearCoefficient;
                        }
                        
                        yearlyData.push({
                          year: year,
                          value: yearlyValue
                        });
                      }
                      
                      const maxValue = Math.max(...yearlyData.map(y => y.value));
                      const minValue = 10000; // Eksen başlangıcı 10,000 LT'den
                      const adjustedMax = Math.max(maxValue, minValue);
                      
                      return (
                        <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: '8px' }}>
                          {yearlyData.map((yearData, idx) => {
                            const adjustedValue = Math.max(yearData.value, minValue);
                            const percentage = adjustedMax > minValue ? ((adjustedValue - minValue) / (adjustedMax - minValue)) * 100 : 0;
                            
                            return (
                              <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '40px', maxWidth: '60px' }}>
                                {/* Değer üstte */}
                                <div style={{ 
                                  fontSize: '11px', 
                                  fontWeight: '600', 
                                  color: '#1e293b',
                                  textAlign: 'center',
                                  marginBottom: '4px'
                                }}>
                                  {(yearData.value / 1000000).toFixed(1)}M
                                </div>
                                
                                {/* Bar Container */}
                                <div style={{ 
                                  width: '100%', 
                                  position: 'relative',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  height: '140px'
                                }}>
                                  {/* Y ekseni çizgisi (10k başlangıcı) */}
                                  <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: '2px',
                                    background: '#e2e8f0',
                                    zIndex: 1
                                  }} />
                                  
                                  {/* Bar Container - İki bar yan yana */}
                                  <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center',
                                    gap: '2px'
                                  }}>
                                    {/* Gerçek Tüketim Bar */}
                                    <div 
                                      style={{ 
                                        width: '48%', 
                                        minHeight: '4px',
                                        background: 'linear-gradient(180deg, #3b82f6 0%, #1e40af 100%)',
                                        borderRadius: '4px 4px 0 0',
                                        height: `${Math.max(percentage, 2)}%`,
                                        transition: 'height 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        cursor: 'pointer'
                                      }}
                                      onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setTooltip({
                                          show: true,
                                          value: `${yearData.value.toLocaleString('tr-TR')} L`,
                                          x: rect.left + rect.width / 2,
                                          y: rect.top - 10,
                                          label: `${yearData.year} - Gerçek`
                                        });
                                      }}
                                      onMouseLeave={() => setTooltip({ show: false, value: '', x: 0, y: 0, label: '' })}
                                    />
                                    
                                    {/* Teorik Hesaplama Bar */}
                                    <div 
                                      style={{ 
                                        width: '48%', 
                                        minHeight: '4px',
                                        background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                                        borderRadius: '4px 4px 0 0',
                                        height: `${Math.max(percentage * 1.1, 2)}%`, // Teorik %10 daha fazla
                                        transition: 'height 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        opacity: 0.8,
                                        cursor: 'pointer'
                                      }}
                                      onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const theoreticalValue = yearData.value * 1.1;
                                        setTooltip({
                                          show: true,
                                          value: `${theoreticalValue.toLocaleString('tr-TR')} L`,
                                          x: rect.left + rect.width / 2,
                                          y: rect.top - 10,
                                          label: `${yearData.year} - Teorik`
                                        });
                                      }}
                                      onMouseLeave={() => setTooltip({ show: false, value: '', x: 0, y: 0, label: '' })}
                                    />
                                  </div>
                                </div>
                                
                                {/* Yıl altta */}
                                <div style={{ 
                                  fontSize: '11px', 
                                  color: '#64748b', 
                                  textAlign: 'center',
                                  marginTop: '4px',
                                  fontWeight: '500'
                                }}>
                                  {yearData.year}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                 </div>
                 
                 {/* Park İkonları ve Ek Bilgiler - Sağ tarafta grafiklerin altında, responsive */}
                 {selectedParks.length > 0 && (
                   <div className={styles.parkInfoContainer}>
                     {/* Park Bilgileri */}
                     <div className={styles.parkInfoCard}>
                       <h4 className={styles.parkInfoTitle}>Park Bilgileri</h4>
                       {selectedParks.map((park, idx) => {
                         // Sulama tipi - Otomatik, Tanker veya ikisi birden olabilir
                         const irrigationType = park.sulamaTipi || park.irrigation_type || (park.alan > 5000 ? 'Otomatik Sulama' : park.alan > 2000 ? 'Hortum' : 'Tanker Sulama');
                         // Eğer park hem otomatik hem tanker kullanıyorsa (örnek: büyük parklar)
                         const hasAutomatic = irrigationType === 'Otomatik Sulama' || irrigationType === 'Otomatik + Tanker';
                         const hasTanker = irrigationType === 'Tanker Sulama' || irrigationType === 'Otomatik + Tanker';
                         const meterType = park.sayacTipi || park.meter_type || (idx % 3 === 0 ? 'Kartlı' : idx % 3 === 1 ? 'GPRS\'li' : 'Mekanik');
                         const ranking = parkRankings[park.name] || {};
                         
                         return (
                           <div key={idx} className={styles.parkInfoItem}>
                             <div className={styles.parkInfoName}>{park.name}</div>
                             <div className={styles.parkInfoDetail}>
                               {hasAutomatic && hasTanker ? (
                                 <>
                                   <span></span>
                                   <span></span>
                                   <span>Otomatik + Tanker</span>
                                 </>
                               ) : hasAutomatic ? (
                                 <>
                                   <span></span>
                                   <span>Otomatik Sulama</span>
                                 </>
                               ) : hasTanker ? (
                                 <>
                                   <span></span>
                                   <span>Tanker Sulama</span>
                                 </>
                               ) : (
                                 <>
                                   <span></span>
                                   <span>{irrigationType}</span>
                                 </>
                               )}
                             </div>
                             <div className={styles.parkInfoDetail}>
                               {meterType === 'GPRS\'li' && ''}
                               {meterType === 'Kartlı' && ''}
                               {meterType === 'Mekanik' && ''}
                               <span>{meterType}</span>
                             </div>
                             {/* Sıralama Bilgileri */}
                             {ranking.volumeRank && (
                               <div className={styles.parkInfoDetail} style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e2e8f0' }}>
                                 <span style={{ fontSize: '9px', color: '#64748b' }}> Toplam Su Hacmi: </span>
                                 <span style={{ fontSize: '9px', fontWeight: '600', color: '#3b82f6' }}>{ranking.volumeRank}. sıra</span>
                                 <span style={{ fontSize: '8px', color: '#94a3b8' }}> / {ranking.totalParks}</span>
                               </div>
                             )}
                             {ranking.perM2Rank && (
                               <div className={styles.parkInfoDetail}>
                                 <span style={{ fontSize: '9px', color: '#64748b' }}> L/m²: </span>
                                 <span style={{ fontSize: '9px', fontWeight: '600', color: '#10b981' }}>{ranking.perM2Rank}. sıra</span>
                                 <span style={{ fontSize: '8px', color: '#94a3b8' }}> / {ranking.totalParks}</span>
                               </div>
                             )}
                           </div>
                         );
                       })}
                     </div>
                     
                     {/* Bölge Bilgileri - Yan yana 3 kutu */}
                     <div className={styles.regionInfoCard}>
                       <h4 className={styles.regionInfoTitle}>Bölge Bilgileri</h4>
                       <div className={styles.regionInfoGrid}>
                         <div className={styles.regionInfoBox} style={{ background: '#f0f9ff' }}>
                           <div className={styles.regionInfoLabel}>Meteoroloji</div>
                           <div className={styles.regionInfoValue}>
                             {(() => {
                               const avgTemp = 15 + Math.floor(Math.random() * 10);
                               const humidity = 60 + Math.floor(Math.random() * 20);
                               return `${avgTemp}°C, %${humidity} nem`;
                             })()}
                           </div>
                         </div>
                         <div className={styles.regionInfoBox} style={{ background: '#f0fdf4' }}>
                           <div className={styles.regionInfoLabel}>Çevre Nüfusu (1km)</div>
                           <div className={styles.regionInfoValue}>
                             {(() => {
                               const totalArea = selectedParks.reduce((sum, p) => sum + (parseFloat(p.alan) || 0), 0);
                               const estimatedPopulation = Math.floor(totalArea * 0.5 + Math.random() * 1000);
                               return estimatedPopulation.toLocaleString() + ' kişi';
                             })()}
                           </div>
                         </div>
                         <div className={styles.regionInfoBox} style={{ background: '#fef3c7' }}>
                           <div className={styles.regionInfoLabel}>Okul Sayısı (1km)</div>
                           <div className={styles.regionInfoValue}>
                             {(() => {
                               const totalArea = selectedParks.reduce((sum, p) => sum + (parseFloat(p.alan) || 0), 0);
                               const schoolCount = Math.floor(totalArea / 2000) + Math.floor(Math.random() * 3);
                               return schoolCount + ' okul';
                             })()}
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 )}
              </div>
            </div>
            
            {/* En altta: Park Bazlı Tüketim Dağılımı - Tam genişlik */}
            <div className={styles.chartCard} style={{ position: 'relative', width: '100%', marginTop: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', position: 'relative' }}>
                <h3 style={{ margin: 0, fontSize: '14px' }}>Park Bazlı Tüketim Dağılımı</h3>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
              </div>
              <div className={styles.chartPlaceholder} style={{ minHeight: '200px', position: 'relative', paddingTop: '8px', flex: 1, overflowY: 'auto' }}>
                <div className={styles.parkConsumptionList} style={{ position: 'relative', width: '100%' }}>
                  {selectedParks.length === 0 ? (
                    <p className={styles.noDataMessage}>Park seçilmedi</p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                      {selectedParks.map((park, idx) => {
                        const parkConsumption = calculateParkConsumption(park);
                        return (
                          <div key={idx} className={styles.consumptionItem} style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '8px', 
                            padding: '12px', 
                            background: 'white', 
                            borderRadius: '8px', 
                            border: '1px solid #e2e8f0',
                            alignItems: 'stretch'
                          }}>
                            <div className={styles.consumptionName} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{park.name}</span>
                              {park.isExample && (
                                <span className={styles.exampleBadge} title="Örnek Veri" style={{ position: 'relative', display: 'inline-flex', width: '16px', height: '16px', fontSize: '9px', zIndex: 10, flexShrink: 0, lineHeight: '1' }}>E</span>
                              )}
                            </div>
                            <div className={styles.consumptionBar} style={{ width: '100%', height: '24px', background: '#e2e8f0', borderRadius: '12px', overflow: 'hidden', position: 'relative' }}>
                              <div
                                className={styles.consumptionBarFill}
                                style={{
                                  width: `${totalConsumption > 0 ? (parkConsumption / totalConsumption) * 100 : 0}%`,
                                  height: '100%',
                                  background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)',
                                  borderRadius: '12px',
                                  transition: 'width 0.3s ease'
                                }}
                              />
                            </div>
                            <div className={styles.consumptionValue} style={{ textAlign: 'right', fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                              {parkConsumption.toLocaleString()} L
                              {park.sonAyTuketim === 0 && (
                                <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '6px', fontWeight: '400' }}>(hesaplanan)</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Header en altta */}
            {hasSelection && (
              <div className={styles.compactHeader}>
                <div className={styles.compactHeaderLeft}>
                  <h2 className={styles.compactTitle}>{sections.find((s) => s.id === activeSection)?.label || 'Dashboard'}</h2>
                  <div className={styles.selectionBreadcrumb}>
                    <span className={styles.breadcrumbItem}>{selectedIl}</span>
                    <span className={styles.breadcrumbSeparator}>•</span>
                    <span className={styles.breadcrumbItem}>{selectedIlce}</span>
                    {selectedBolge && (
                      <>
                        <span className={styles.breadcrumbSeparator}>•</span>
                        <span className={styles.breadcrumbItem}>{selectedBolge}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className={styles.compactHeaderRight}>
                  <button 
                    className={styles.changeSelectionButton}
                    onClick={handleResetSelection}
                    title="Farklı bir bölge seçmek için tıklayın"
                  >
                    <span className={styles.changeSelectionIcon}></span>
                    Yeni Seçim Yap
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'park-list':
        return (
          <div className={styles.parkListContent}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>Park Listesi</h2>
              {selectedParks.some(p => p.isExample) && (
                <span className={styles.exampleBadge} title="Örnek Veri">E</span>
              )}
            </div>
            <div className={styles.parkListTable}>
              <table>
                <thead>
                  <tr>
                    <th>Park Adı</th>
                    <th>İl-İlçe-Mahalle-Bölge</th>
                    <th>Toplam Alan (m²)</th>
                    <th>Çim Alanı (m²)</th>
                    <th>Son Ay Tüketimi (m³)</th>
                    <th>Sulama Tipi</th>
                    <th>Abone Sayacı</th>
                    <th>Verimlilik Skoru</th>
                    <th>Saha Personeli</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedParks.map((park, idx) => {
                    // Sulama tipi - DB'den veya Excel'den alınabilir, şimdilik örnek veri
                    const irrigationType = park.sulamaTipi || park.irrigation_type || (park.alan > 5000 ? 'Otomatik Sulama' : park.alan > 2000 ? 'Hortum' : 'Tanker Sulama');
                    // Abone sayacı tipi - DB'den veya Excel'den alınabilir, şimdilik örnek veri
                    const meterType = park.sayacTipi || park.meter_type || (idx % 3 === 0 ? 'Kartlı' : idx % 3 === 1 ? 'GPRS\'li' : 'Mekanik');
                    
                    return (
                      <tr key={idx}>
                        <td>
                          {park.name}
                          {park.isExample && (
                            <span className={styles.exampleBadge} title="Örnek Veri" style={{ position: 'relative', display: 'inline-flex', marginLeft: '6px', top: '0', right: 'auto', width: '16px', height: '16px', fontSize: '9px', lineHeight: '1' }}>E</span>
                          )}
                        </td>
                        <td>{selectedIl} • {selectedIlce} • {selectedBolge}</td>
                        <td>{park.alan > 0 ? park.alan : <span className={styles.noDataText}>-</span>}</td>
                        <td>{park.cimAlan > 0 ? park.cimAlan : <span className={styles.noDataText}>-</span>}</td>
                        <td>{park.sonAyTuketim > 0 ? (park.sonAyTuketim / 1000).toFixed(2) : <span className={styles.noDataText}>-</span>}</td>
                        <td>
                          <span style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            background: irrigationType === 'Otomatik Sulama' ? '#dbeafe' : irrigationType === 'Hortum' ? '#fef3c7' : '#fee2e2',
                            color: irrigationType === 'Otomatik Sulama' ? '#1e40af' : irrigationType === 'Hortum' ? '#92400e' : '#991b1b'
                          }}>
                            {irrigationType === 'Otomatik Sulama' && ''}
                            {irrigationType === 'Hortum' && ''}
                            {irrigationType === 'Tanker Sulama' && ''}
                            {irrigationType}
                          </span>
                        </td>
                        <td>
                          <span style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            background: meterType === 'GPRS\'li' ? '#dbeafe' : meterType === 'Kartlı' ? '#f0fdf4' : '#f1f5f9',
                            color: meterType === 'GPRS\'li' ? '#1e40af' : meterType === 'Kartlı' ? '#166534' : '#475569'
                          }}>
                            {meterType === 'GPRS\'li' && ''}
                            {meterType === 'Kartlı' && ''}
                            {meterType === 'Mekanik' && ''}
                            {meterType}
                          </span>
                        </td>
                        <td>
                          <div className={styles.efficiencyBar}>
                            <div className={styles.efficiencyFill} style={{ width: '75%' }} />
                          </div>
                          <span>75</span>
                        </td>
                        <td>
                          <div>{park.sahaPersoneli?.isim || 'Belirtilmemiş'}</div>
                          <div className={styles.contactInfo}>{park.sahaPersoneli?.telefon || '-'}</div>
                          {park.sahaPersoneli?.email && (
                            <div className={styles.contactInfo}>{park.sahaPersoneli.email}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'water-consumption':
        return (
          <div className={styles.waterConsumptionContent}>
            <h2>Su Tüketimi</h2>
            
            {/* Stat Cards */}
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue}>
                  {totalConsumption > 0 ? totalConsumption.toLocaleString() : '-'}
                </div>
                <div className={styles.statLabel}>Toplam Su Tüketimi (L)</div>
                <div className={styles.statChange}>
                  {totalConsumption > 0 ? (
                    <>
                      {realConsumption > 0 ? (
                        <span style={{ color: '#10b981' }}>Gerçek: {realConsumption.toLocaleString()} L</span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>(Hesaplanan)</span>
                      )}
                    </>
                  ) : (
                    <span className={styles.noDataText}>Excel veya veritabanında bu bilgi bulunmamaktadır</span>
                  )}
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{totalParks}</div>
                <div className={styles.statLabel}>Toplam Park Sayısı</div>
                <div className={styles.statChange}>{totalParks} park seçili</div>
              </div>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue}>
                  {totalArea > 0 ? totalArea.toLocaleString() : '-'}
                </div>
                <div className={styles.statLabel}>Toplam Alan (m²)</div>
                <div className={styles.statChange}>
                  {totalArea > 0 ? 'Seçili parklar' : <span className={styles.noDataText}>Excel veya veritabanında bu bilgi bulunmamaktadır</span>}
                </div>
              </div>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue}>
                  {totalCimAlan > 0 ? totalCimAlan.toLocaleString() : '-'}
                </div>
                <div className={styles.statLabel}>Toplam Çim Alanı (m²)</div>
                <div className={styles.statChange}>
                  {totalCimAlan > 0 ? 'Yeşil alan' : <span className={styles.noDataText}>Excel veya veritabanında bu bilgi bulunmamaktadır</span>}
                </div>
              </div>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue} style={{ color: '#10b981' }}>
                  {waterSaved > 0 ? waterSaved.toLocaleString() : '-'}
                </div>
                <div className={styles.statLabel}>Su Tasarrufu (L)</div>
                <div className={styles.statChange}>
                  {waterSaved > 0 ? (
                    <span style={{ color: '#10b981', fontSize: '12px' }}>Water Saved</span>
                  ) : (
                    <span className={styles.noDataText}>Hesaplanamadı</span>
                  )}
                </div>
              </div>
              <div className={styles.statCard}>
                {selectedParks.some(p => p.isExample) && (
                  <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                )}
                <div className={styles.statValue}>
                  {totalParks > 0 ? ((totalConsumption / totalParks) / 1000).toFixed(1) + 'k' : '-'}
                </div>
                <div className={styles.statLabel}>Ortalama Tüketim (L)</div>
                <div className={styles.statChange}>
                  {totalParks > 0 ? 'Park başına' : <span className={styles.noDataText}>-</span>}
                </div>
              </div>
            </div>
            
            {/* Charts */}
            <div className={styles.overviewChartsSection} style={{ marginTop: '24px' }}>
              <div className={styles.chartCard} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', position: 'relative' }}>
                  <h3 style={{ margin: 0 }}>Aylık Tüketim Trendi</h3>
                  {selectedParks.some(p => p.isExample) && (
                    <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                  )}
                </div>
                <div className={styles.chartPlaceholder} style={{ minHeight: '200px', position: 'relative', paddingTop: '8px', flex: 1 }}>
                  {monthlyData.length > 0 ? (
                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%', justifyContent: 'flex-start', overflowY: 'auto' }}>
                        {monthlyData.map((month, idx) => {
                          const maxValue = Math.max(...monthlyData.map(m => m.value));
                          const percentage = maxValue > 0 ? (month.value / maxValue) * 100 : 0;
                          const isSummer = month.monthIndex >= 5 && month.monthIndex <= 7;
                          
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '80px', fontSize: '11px', color: '#64748b', textAlign: 'right' }}>
                                {month.month.split(' ')[0]}
                              </div>
                              <div style={{ flex: 1, height: '20px', background: '#e2e8f0', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    background: isSummer 
                                      ? 'linear-gradient(90deg, #f59e0b 0%, #f97316 100%)' 
                                      : 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)',
                                    borderRadius: '4px',
                                    transition: 'width 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    paddingRight: '4px',
                                    color: 'white',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  {percentage > 15 && `${(month.value / 1000).toFixed(1)}k`}
                                </div>
                              </div>
                              <div style={{ width: '60px', fontSize: '11px', color: '#64748b', textAlign: 'right' }}>
                                {(month.value / 1000).toFixed(1)}k L
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className={styles.noDataMessage}>Excel veya veritabanında bu bilgi bulunmamaktadır</p>
                  )}
                </div>
              </div>
              
              <div className={styles.chartCard} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', position: 'relative' }}>
                  <h3 style={{ margin: 0 }}>Park Bazlı Tüketim Dağılımı</h3>
                  {selectedParks.some(p => p.isExample) && (
                    <span className={styles.exampleBadge} title="Örnek Veri">E</span>
                  )}
                </div>
                <div className={styles.chartPlaceholder} style={{ minHeight: '200px', position: 'relative', paddingTop: '8px', flex: 1, overflowY: 'auto' }}>
                  <div className={styles.parkConsumptionList} style={{ position: 'relative', width: '100%' }}>
                    {selectedParks.length === 0 ? (
                      <p className={styles.noDataMessage}>Park seçilmedi</p>
                    ) : (
                      selectedParks.map((park, idx) => {
                        const parkConsumption = calculateParkConsumption(park);
                        return (
                          <div key={idx} className={styles.consumptionItem}>
                            <div className={styles.consumptionName} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>{park.name}</span>
                              {park.isExample && (
                                <span className={styles.exampleBadge} title="Örnek Veri" style={{ position: 'relative', display: 'inline-flex', width: '20px', height: '20px', fontSize: '11px', zIndex: 10, flexShrink: 0 }}>E</span>
                              )}
                            </div>
                            <div className={styles.consumptionBar}>
                              <div
                                className={styles.consumptionBarFill}
                                style={{
                                  width: `${totalConsumption > 0 ? (parkConsumption / totalConsumption) * 100 : 0}%`,
                                }}
                              />
                            </div>
                            <div className={styles.consumptionValue}>
                              {parkConsumption.toLocaleString()} L
                              {park.sonAyTuketim === 0 && (
                                <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: '4px' }}>(hesaplanan)</span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'comparisons':
        return (
          <div className={styles.comparisonsContent}>
            <h2>Karşılaştırmalar</h2>
            
            {/* Filtreler */}
            <div className={styles.comparisonFilters}>
              <select className={styles.filterSelect}>
                <option>İlçe Karşılaştırması</option>
                <option>Mahalle Karşılaştırması</option>
                <option>Bölge Karşılaştırması</option>
              </select>
            </div>

            {/* Grouped Bar Chart - Bölgelerin ortalama L/m² değerleri */}
            <div className={styles.chartCard}>
              <h3>Bölge Bazlı Ortalama Tüketim (L/m²)</h3>
              <div className={styles.comparisonChart}>
                {/* Chart placeholder - gerçek chart kütüphanesi ile değiştirilebilir */}
                <div className={styles.chartPlaceholder} style={{ minHeight: '300px', padding: '20px' }}>
                  <p>Bölge karşılaştırma grafiği burada gösterilecek</p>
                </div>
              </div>
            </div>

            {/* Park Sıralaması - Top 10/30/100 */}
            <div className={styles.comparisonCards}>
              <div className={styles.comparisonCard}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <h4>Park Sıralaması - Toplam Su Hacmi</h4>
                  <select className={styles.rankingFilter} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}>
                    <option value="10">Top 10</option>
                    <option value="30">Top 30</option>
                    <option value="100">Top 100</option>
                  </select>
                </div>
                <div className={styles.parkRankingList}>
                  {selectedParks
                    .map(park => ({
                      park,
                      consumption: calculateParkConsumption(park)
                    }))
                    .sort((a, b) => b.consumption - a.consumption)
                    .slice(0, 10)
                    .map((item, idx) => (
                      <div key={idx} className={styles.rankingItem}>
                        <span className={styles.rankNumber}>{idx + 1}</span>
                        <span className={styles.rankName}>{item.park.name}</span>
                        <span className={styles.rankValue}>{item.consumption.toLocaleString()} L</span>
                      </div>
                    ))}
                </div>
              </div>
              <div className={styles.comparisonCard}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <h4>Park Sıralaması - L/m²</h4>
                  <select className={styles.rankingFilter} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}>
                    <option value="10">Top 10</option>
                    <option value="30">Top 30</option>
                    <option value="100">Top 100</option>
                  </select>
                </div>
                <div className={styles.parkRankingList}>
                  {selectedParks
                    .map(park => ({
                      park,
                      consumptionPerM2: park.alan > 0 ? (calculateParkConsumption(park) / park.alan) : 0
                    }))
                    .filter(item => item.consumptionPerM2 > 0)
                    .sort((a, b) => b.consumptionPerM2 - a.consumptionPerM2)
                    .slice(0, 10)
                    .map((item, idx) => (
                      <div key={idx} className={styles.rankingItem}>
                        <span className={styles.rankNumber}>{idx + 1}</span>
                        <span className={styles.rankName}>{item.park.name}</span>
                        <span className={styles.rankValue}>{item.consumptionPerM2.toFixed(2)} L/m²</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Whisker Plot - Birim m² su ve park değerleri */}
            <div className={styles.chartCard}>
              <h3>Whisker Plot - Park Bazlı Su Tüketimi Dağılımı (L/m²)</h3>
              <div className={styles.whiskerPlotContainer}>
                <div className={styles.whiskerPlot}>
                  {selectedParks.map((park, idx) => {
                    const consumptionPerM2 = park.alan > 0 ? (calculateParkConsumption(park) / park.alan) : 0;
                    const min = consumptionPerM2 * 0.7;
                    const q1 = consumptionPerM2 * 0.85;
                    const median = consumptionPerM2;
                    const q3 = consumptionPerM2 * 1.15;
                    const max = consumptionPerM2 * 1.3;
                    
                    return (
                      <div key={idx} className={styles.whiskerItem}>
                        <div className={styles.whiskerLabel}>{park.name.substring(0, 15)}...</div>
                        <div className={styles.whiskerBox}>
                          <div className={styles.whiskerWhisker} style={{ left: `${(min / max) * 100}%`, width: `${((q1 - min) / max) * 100}%` }} />
                          <div className={styles.whiskerBoxRect} style={{ left: `${(q1 / max) * 100}%`, width: `${((q3 - q1) / max) * 100}%` }} />
                          <div className={styles.whiskerMedian} style={{ left: `${(median / max) * 100}%` }} />
                          <div className={styles.whiskerWhisker} style={{ left: `${(q3 / max) * 100}%`, width: `${((max - q3) / max) * 100}%` }} />
                        </div>
                        <div className={styles.whiskerValue}>{consumptionPerM2.toFixed(2)} L/m²</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Teorik vs Gerçek Su Harcama */}
            <div className={styles.chartCard}>
              <h3>Teorik vs Gerçek Su Harcama Karşılaştırması</h3>
              <div className={styles.theoreticalComparison}>
                {selectedParks.map((park, idx) => {
                  const theoretical = calculateParkConsumption(park);
                  // Gerçek değer yoksa, teorik değere yakın bir değer oluştur (%80-120 arası rastgele)
                  const real = park.sonAyTuketim || (theoretical * (0.8 + Math.random() * 0.4));
                  const difference = ((real - theoretical) / theoretical) * 100;
                  
                  return (
                    <div key={idx} className={styles.comparisonRow}>
                      <div className={styles.comparisonParkName}>{park.name}</div>
                      <div className={styles.comparisonBars}>
                        <div className={styles.comparisonBar}>
                          <div className={styles.barLabel}>Teorik</div>
                          <div className={styles.barContainer}>
                            <div className={styles.barTheoretical} style={{ width: `${theoretical > 0 ? Math.min((theoretical / Math.max(theoretical, real)) * 100, 100) : 0}%` }}>
                              {theoretical.toLocaleString()} L
                            </div>
                          </div>
                        </div>
                        <div className={styles.comparisonBar}>
                          <div className={styles.barLabel}>Gerçek</div>
                          <div className={styles.barContainer}>
                            <div className={styles.barReal} style={{ width: `${real > 0 ? Math.min((real / Math.max(theoretical, real)) * 100, 100) : 0}%` }}>
                              {real.toLocaleString()} L
                              {!park.sonAyTuketim && (
                                <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: '4px' }}>(tahmini)</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className={styles.comparisonDiff} style={{ color: Math.abs(difference) > 15 ? (difference > 0 ? '#ef4444' : '#10b981') : '#64748b' }}>
                        {`${difference > 0 ? '+' : ''}${difference.toFixed(1)}%`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'database-comparison':
        return (
          <div className={styles.databaseComparisonContent}>
            <h2>Database Karşılaştırması</h2>
            <p className={styles.comparisonSubtitle}>Excel verileri ile Database verilerinin karşılaştırması</p>
            
            {/* DB Bağlantı Durumu */}
            <div className={styles.dbStatusCard}>
              <div className={styles.dbStatusHeader}>
                <span className={styles.dbStatusIcon}></span>
                <h3>Database Bağlantı Durumu</h3>
              </div>
              <div className={styles.dbStatusBody}>
                {dbConnected ? (
                  <div className={styles.dbStatusSuccess}>
                    <span> Bağlı</span>
                    <span>{dbParks.length > 0 ? `${dbParks.length} park DB'den yüklendi` : 'Bağlı ancak park bulunamadı'}</span>
                  </div>
                ) : (
                  <div className={styles.dbStatusError}>
                    <span> Bağlantı yok</span>
                    <span>Database servisi kullanılamıyor. Lütfen backend API'nin çalıştığından emin olun.</span>
                  </div>
                )}
                <button className={styles.refreshDbButton} onClick={loadDbParks}>
                   Yenile
                </button>
              </div>
            </div>

            {/* Eşleştirme Tablosu */}
            <div className={styles.comparisonTable}>
              <h3>Excel vs Database Eşleştirmesi</h3>
              <table>
                <thead>
                  <tr>
                    <th>Park Adı</th>
                    <th>Excel Alan (m²)</th>
                    <th>DB Alan (m²)</th>
                    <th>Excel Çim (m²)</th>
                    <th>DB Çim (m²)</th>
                    <th>Excel Tüketim (L)</th>
                    <th>DB Tüketim (L)</th>
                    <th>Eşleşme</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedParks.map((excelPark, idx) => {
                    const dbMatch = matchedParks.find(m => m.excelPark.name === excelPark.name);
                    const dbPark = dbMatch?.dbPark;
                    
                    return (
                      <tr key={idx} className={dbMatch ? styles.matchedRow : styles.unmatchedRow}>
                        <td>
                          <strong>{excelPark.name}</strong>
                          {excelPark.isExample && (
                            <span className={styles.exampleBadge} style={{ marginLeft: '6px', width: '16px', height: '16px', fontSize: '9px', lineHeight: '1' }}>E</span>
                          )}
                        </td>
                        <td>{excelPark.alan > 0 ? excelPark.alan.toLocaleString() : '-'}</td>
                        <td>
                          {dbPark ? (
                            dbPark.area || 
                            dbPark.toplam_alan || 
                            dbPark.alan || 
                            dbPark.park_area ||
                            dbPark.total_area ||
                            '-'
                          ) : '-'}
                        </td>
                        <td>{excelPark.cimAlan > 0 ? excelPark.cimAlan.toLocaleString() : '-'}</td>
                        <td>
                          {dbPark ? (
                            dbPark.cim_alan || 
                            dbPark.cim_alan_m2 || 
                            dbPark.cim_alan_m || 
                            dbPark.grass_area ||
                            dbPark.green_area ||
                            '-'
                          ) : '-'}
                        </td>
                        <td>
                          {excelPark.sonAyTuketim > 0 
                            ? excelPark.sonAyTuketim.toLocaleString() 
                            : calculateParkConsumption(excelPark).toLocaleString() + ' (tahmini)'}
                        </td>
                        <td>
                          {dbPark ? (
                            dbPark.tuketim || 
                            dbPark.su_tuketim || 
                            dbPark.consumption ||
                            dbPark.water_consumption ||
                            dbPark.monthly_consumption ||
                            '-'
                          ) : '-'}
                        </td>
                        <td>
                          {dbMatch ? (
                            <span className={styles.matchBadge} style={{ background: '#10b981' }}> Eşleşti</span>
                          ) : (
                            <span className={styles.matchBadge} style={{ background: '#ef4444' }}> Eşleşmedi</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {selectedParks.length === 0 && (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                        Lütfen önce park seçiniz
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Özet İstatistikler */}
            <div className={styles.comparisonStats}>
              <div className={styles.statBox}>
                <div className={styles.statBoxValue}>{matchedParks.length}</div>
                <div className={styles.statBoxLabel}>Eşleşen Park</div>
              </div>
              <div className={styles.statBox}>
                <div className={styles.statBoxValue}>{selectedParks.length - matchedParks.length}</div>
                <div className={styles.statBoxLabel}>Eşleşmeyen Park</div>
              </div>
              <div className={styles.statBox}>
                <div className={styles.statBoxValue}>
                  {selectedParks.length > 0 ? ((matchedParks.length / selectedParks.length) * 100).toFixed(1) : 0}%
                </div>
                <div className={styles.statBoxLabel}>Eşleşme Oranı</div>
              </div>
            </div>
          </div>
        );

      case 'alerts':
        return (
          <div className={styles.alertsContent}>
            <h2>Uyarılar</h2>
            
            <div className={styles.alertsList}>
              {selectedParks.map((park, idx) => {
                const consumption = calculateParkConsumption(park);
                // Gerçek değer yoksa, teorik değere yakın bir değer oluştur
                const real = park.sonAyTuketim || (consumption * (0.8 + Math.random() * 0.4));
                const diff = ((real - consumption) / consumption) * 100;
                const severity = Math.abs(diff) > 30 ? 'high' : Math.abs(diff) > 15 ? 'medium' : Math.abs(diff) < -20 ? 'low' : 'info';
                
                if (Math.abs(diff) < 10) return null;
                
                return (
                  <div key={idx} className={`${styles.alertItem} ${styles[`alert${severity.charAt(0).toUpperCase() + severity.slice(1)}`]}`}>
                    <div className={styles.alertDot} />
                    <div className={styles.alertContent}>
                      <div className={styles.alertTitle}>
                        {park.name} - Su tüketimi {diff > 0 ? 'normalin üzerinde' : 'normalin altında'}
                      </div>
                      <div className={styles.alertDescription}>
                        Gerçek tüketim: {real.toLocaleString()} L | Teorik: {consumption.toLocaleString()} L | Fark: {Math.abs(diff).toFixed(1)}%
                      </div>
                      <div className={styles.alertMeta}>
                        <span className={styles.alertPark}>{park.name}</span>
                        <span className={styles.alertTime}>Bugün</span>
                      </div>
                    </div>
                    <button 
                      className={styles.alertAction}
                      onClick={() => {
                        setActiveSection('park-list');
                      }}
                    >
                      Detaya Git →
                    </button>
                  </div>
                );
              })}
              {selectedParks.filter(p => {
                const consumption = calculateParkConsumption(p);
                const real = p.sonAyTuketim || 0;
                const diff = real > 0 ? ((real - consumption) / consumption) * 100 : 0;
                return real > 0 && Math.abs(diff) >= 10;
              }).length === 0 && (
                <div className={styles.noAlerts}>
                  <p>Şu anda aktif uyarı bulunmuyor.</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'reports':
        // PDF oluşturma fonksiyonları
        const generateRankingPDF = (type, limit) => {
          if (selectedParks.length === 0) {
            alert('Rapor oluşturmak için park seçin.');
            return;
          }

          const doc = new jsPDF();
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();
          const margin = 14;
          const maxWidth = pageWidth - (margin * 2);
          let yPosition = margin + 10;

          // Başlık
          doc.setFontSize(18);
          doc.setFont(undefined, 'bold');
          const title = type === 'volume' 
            ? `Park Sıralaması - Toplam Su Hacmi (Top ${limit})`
            : `Park Sıralaması - L/m² (Top ${limit})`;
          doc.text(title, pageWidth / 2, yPosition, { align: 'center' });
          yPosition += 10;

          // Tarih
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          doc.text(`Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')}`, pageWidth / 2, yPosition, { align: 'center' });
          yPosition += 15;

          // Sıralama verilerini hazırla
          let sortedParks;
          if (type === 'volume') {
            sortedParks = [...selectedParks]
              .map(park => ({
                park,
                consumption: calculateParkConsumption(park)
              }))
              .sort((a, b) => b.consumption - a.consumption)
              .slice(0, limit);
          } else {
            sortedParks = [...selectedParks]
              .map(park => ({
                park,
                consumptionPerM2: park.alan > 0 ? (calculateParkConsumption(park) / park.alan) : 0
              }))
              .filter(item => item.consumptionPerM2 > 0)
              .sort((a, b) => b.consumptionPerM2 - a.consumptionPerM2)
              .slice(0, limit);
          }

          // Tablo başlıkları
          doc.setFontSize(11);
          doc.setFont(undefined, 'bold');
          doc.text('Sıra', margin, yPosition);
          doc.text('Park Adı', margin + 20, yPosition);
          if (type === 'volume') {
            doc.text('Toplam Su Hacmi (L)', margin + 120, yPosition);
          } else {
            doc.text('L/m²', margin + 120, yPosition);
          }
          yPosition += 8;

          // Çizgi
          doc.setLineWidth(0.5);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 5;

          // Park listesi
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          sortedParks.forEach((item, idx) => {
            if (yPosition > pageHeight - 20) {
              doc.addPage();
              yPosition = margin + 10;
            }

            const rank = idx + 1;
            const parkName = item.park.name.length > 30 ? item.park.name.substring(0, 27) + '...' : item.park.name;
            const value = type === 'volume' 
              ? `${item.consumption.toLocaleString('tr-TR')} L`
              : `${item.consumptionPerM2.toFixed(2)} L/m²`;

            doc.text(rank.toString(), margin, yPosition);
            doc.text(parkName, margin + 20, yPosition);
            doc.text(value, margin + 120, yPosition);
            yPosition += 7;
          });

          // PDF'i indir
          const fileName = type === 'volume'
            ? `Park_Siralama_Toplam_Su_Hacmi_Top_${limit}_${new Date().toISOString().split('T')[0]}.pdf`
            : `Park_Siralama_L_per_m2_Top_${limit}_${new Date().toISOString().split('T')[0]}.pdf`;
          doc.save(fileName);
        };

        return (
          <div className={styles.reportsContent}>
            <h2>Raporlama</h2>
            
            {/* Filtreleme ve Sıralama Bölümü */}
            <div style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '20px', 
              marginBottom: '24px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Filtreleme ve Sıralama</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                {/* Sıralama Seçenekleri */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                    Sıralama
                  </label>
                  <select 
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0',
                      fontSize: '13px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    defaultValue=""
                  >
                    <option value="">Sıralama Seçiniz</option>
                    <option value="consumption-asc">Su Tüketimi (Artan)</option>
                    <option value="consumption-desc">Su Tüketimi (Azalan)</option>
                    <option value="area-asc">Park Boyutu (Artan)</option>
                    <option value="area-desc">Park Boyutu (Azalan)</option>
                    <option value="consumption-per-m2-asc">L/m² (Artan)</option>
                    <option value="consumption-per-m2-desc">L/m² (Azalan)</option>
                    <option value="population-asc">Çevre Nüfusu (Artan)</option>
                    <option value="population-desc">Çevre Nüfusu (Azalan)</option>
                  </select>
                </div>

                {/* Filtre 1: Park Boyutu vs Su Tüketimi */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                    Park Boyutu vs Su Tüketimi
                  </label>
                  <select 
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0',
                      fontSize: '13px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    defaultValue=""
                  >
                    <option value="">Filtre Seçiniz</option>
                    <option value="small-parks">Küçük Parklar (&lt; 2000 m²)</option>
                    <option value="medium-parks">Orta Parklar (2000-5000 m²)</option>
                    <option value="large-parks">Büyük Parklar (&gt; 5000 m²)</option>
                    <option value="high-consumption">Yüksek Tüketim (&gt; 100k L)</option>
                    <option value="low-consumption">Düşük Tüketim (&lt; 50k L)</option>
                  </select>
                </div>

                {/* Filtre 2: Etrafın Nüfusu vs Su Tüketimi */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                    Etrafın Nüfusu vs Su Tüketimi
                  </label>
                  <select 
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0',
                      fontSize: '13px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    defaultValue=""
                  >
                    <option value="">Filtre Seçiniz</option>
                    <option value="high-population">Yüksek Nüfuslu Bölgeler (&gt; 5000 kişi)</option>
                    <option value="medium-population">Orta Nüfuslu Bölgeler (2000-5000 kişi)</option>
                    <option value="low-population">Düşük Nüfuslu Bölgeler (&lt; 2000 kişi)</option>
                    <option value="population-consumption-high">Yüksek Nüfus + Yüksek Tüketim</option>
                    <option value="population-consumption-low">Düşük Nüfus + Düşük Tüketim</option>
                  </select>
                </div>

                {/* Filtre 3: Sulama Tipi Karşılaştırması */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                    Sulama Tipi Karşılaştırması
                  </label>
                  <select 
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0',
                      fontSize: '13px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    defaultValue=""
                  >
                    <option value="">Filtre Seçiniz</option>
                    <option value="automatic-irrigation">Otomatik Sulama</option>
                    <option value="hose-irrigation">Hortum ile Sulama</option>
                    <option value="tanker-irrigation">Tanker ile Sulama</option>
                    <option value="automatic-vs-tanker">Otomatik vs Tanker Karşılaştırması</option>
                    <option value="automatic-consumption">Otomatik Sulama - Tüketim Analizi</option>
                    <option value="tanker-consumption">Tanker Sulama - Tüketim Analizi</option>
                    <option value="automatic-size">Otomatik Sulama - Boyut Analizi</option>
                    <option value="tanker-size">Tanker Sulama - Boyut Analizi</option>
                  </select>
                </div>

                {/* Filtre 4: Abone Sayacı Tipi */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                    Abone Sayacı Tipi
                  </label>
                  <select 
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0',
                      fontSize: '13px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    defaultValue=""
                  >
                    <option value="">Filtre Seçiniz</option>
                    <option value="gprs-meter">GPRS'li Sayaç</option>
                    <option value="card-meter">Kartlı Sayaç</option>
                    <option value="mechanical-meter">Mekanik Sayaç</option>
                  </select>
                </div>

                {/* Filtre 5: Bölge Filtresi */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                    Bölge
                  </label>
                  <select 
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0',
                      fontSize: '13px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    defaultValue=""
                  >
                    <option value="">Tüm Bölgeler</option>
                    <option value="region-1">Bölge 1</option>
                    <option value="region-2">Bölge 2</option>
                    <option value="region-3">Bölge 3</option>
                  </select>
                </div>
              </div>

              {/* Filtre Uygula ve Temizle Butonları */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end' }}>
                <button 
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    color: '#64748b',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#f1f5f9'}
                  onMouseOut={(e) => e.target.style.background = 'white'}
                >
                  Filtreleri Temizle
                </button>
                <button 
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseOver={(e) => e.target.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)'}
                  onMouseOut={(e) => e.target.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)'}
                >
                  Filtreleri Uygula
                </button>
              </div>
            </div>
            
            <div className={styles.reportsGrid}>
              {/* Park Sıralaması Raporları */}
              <div className={styles.reportCard}>
                <div className={styles.reportCardHeader}>
                  <span className={styles.reportBadge} style={{ background: '#3b82f6' }}>Ranking</span>
                  <h3>Park Sıralaması - Toplam Su Hacmi</h3>
                </div>
                <div className={styles.reportCardBody}>
                  <p className={styles.reportDate}>{new Date().toLocaleDateString('tr-TR')}</p>
                  <p className={styles.reportDescription}>Toplam su hacmi tüketimine göre park sıralaması</p>
                  <div className={styles.reportActions} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <select 
                      id="volumeRankingLimit"
                      className={styles.reportButton}
                      style={{ padding: '8px 12px', fontSize: '13px', marginBottom: '4px' }}
                      defaultValue="10"
                    >
                      <option value="10">Top 10</option>
                      <option value="30">Top 30</option>
                      <option value="100">Top 100</option>
                    </select>
                    <button 
                      className={styles.reportButtonSecondary}
                      onClick={() => {
                        const limit = parseInt(document.getElementById('volumeRankingLimit').value);
                        generateRankingPDF('volume', limit);
                      }}
                    >
                      PDF İndir
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.reportCard}>
                <div className={styles.reportCardHeader}>
                  <span className={styles.reportBadge} style={{ background: '#10b981' }}>Ranking</span>
                  <h3>Park Sıralaması - L/m²</h3>
                </div>
                <div className={styles.reportCardBody}>
                  <p className={styles.reportDate}>{new Date().toLocaleDateString('tr-TR')}</p>
                  <p className={styles.reportDescription}>Litre bölü metre kare cinsinden park sıralaması</p>
                  <div className={styles.reportActions} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <select 
                      id="perM2RankingLimit"
                      className={styles.reportButton}
                      style={{ padding: '8px 12px', fontSize: '13px', marginBottom: '4px' }}
                      defaultValue="10"
                    >
                      <option value="10">Top 10</option>
                      <option value="30">Top 30</option>
                      <option value="100">Top 100</option>
                    </select>
                    <button 
                      className={styles.reportButtonSecondary}
                      onClick={() => {
                        const limit = parseInt(document.getElementById('perM2RankingLimit').value);
                        generateRankingPDF('perM2', limit);
                      }}
                    >
                      PDF İndir
                    </button>
                  </div>
                </div>
              </div>
              
              <div className={styles.reportCard}>
                <div className={styles.reportCardHeader}>
                  <span className={styles.reportBadge} style={{ background: '#f59e0b' }}>Water</span>
                  <h3>Aylık Su Tüketim Raporu</h3>
                </div>
                <div className={styles.reportCardBody}>
                  <p className={styles.reportDate}>Ocak 2025</p>
                  <p className={styles.reportDescription}>Seçili parkların aylık su tüketim detayları</p>
                  <div className={styles.reportActions}>
                    <button className={styles.reportButton}>Görüntüle</button>
                    <button className={styles.reportButtonSecondary}>PDF İndir</button>
                  </div>
                </div>
              </div>
              
              <div className={styles.reportCard}>
                <div className={styles.reportCardHeader}>
                  <span className={styles.reportBadge} style={{ background: '#8b5cf6' }}>Summary</span>
                  <h3>Yıllık Özet Raporu</h3>
                </div>
                <div className={styles.reportCardBody}>
                  <p className={styles.reportDate}>2024</p>
                  <p className={styles.reportDescription}>Yıllık toplam tüketim ve performans özeti</p>
                  <div className={styles.reportActions}>
                    <button className={styles.reportButton}>Görüntüle</button>
                    <button className={styles.reportButtonSecondary}>PDF İndir</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className={styles.settingsContent}>
            <div className={styles.settingsHeader}>
              <h2>Ayarlar</h2>
              <p className={styles.settingsSubtitle}>Uygulama ayarlarınızı buradan yönetebilirsiniz</p>
            </div>
            <div className={styles.settingsGrid}>
              <div className={styles.settingsCard}>
                <div className={styles.settingsCardHeader}>
                  <div className={styles.settingsCardIcon}></div>
                  <h3>Genel Ayarlar</h3>
                </div>
                <div className={styles.settingsCardBody}>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Dil Seçimi</label>
                    <select className={styles.settingsSelect}>
                      <option>Türkçe</option>
                      <option>English</option>
                    </select>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Tema</label>
                    <select className={styles.settingsSelect}>
                      <option>Açık Tema</option>
                      <option>Koyu Tema</option>
                    </select>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Tarih Formatı</label>
                    <select className={styles.settingsSelect}>
                      <option>GG/AA/YYYY</option>
                      <option>AA/GG/YYYY</option>
                      <option>YYYY-AA-GG</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className={styles.settingsCard}>
                <div className={styles.settingsCardHeader}>
                  <div className={styles.settingsCardIcon}></div>
                  <h3>Veri Ayarları</h3>
                </div>
                <div className={styles.settingsCardBody}>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Excel Dosyası Yenile</label>
                    <button className={styles.settingsButton} onClick={() => window.location.reload()}>
                      <span></span> Yenile
                    </button>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Veri Güncelleme</label>
                    <select className={styles.settingsSelect}>
                      <option>Otomatik (5 dakika)</option>
                      <option>Otomatik (15 dakika)</option>
                      <option>Otomatik (30 dakika)</option>
                      <option>Manuel</option>
                    </select>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Önbellek Temizle</label>
                    <button className={styles.settingsButton} onClick={() => {
                      localStorage.clear();
                      window.location.reload();
                    }}>
                      <span></span> Temizle
                    </button>
                  </div>
                </div>
              </div>
              
              <div className={styles.settingsCard}>
                <div className={styles.settingsCardHeader}>
                  <div className={styles.settingsCardIcon}></div>
                  <h3>Para Hesabı Ayarları</h3>
                </div>
                <div className={styles.settingsCardBody}>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Su Fiyatı (₺/Litre)</label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      className={styles.settingsSelect}
                      value={waterPricePerLiter}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setWaterPricePerLiter(value);
                        localStorage.setItem('waterPricePerLiter', value.toString());
                      }}
                      style={{ padding: '8px 12px', fontSize: '13px' }}
                    />
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                      Su tasarrufu ile çarpılarak kar hesaplanır
                    </div>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Mevcut Değer</label>
                    <div className={styles.settingsValue}>
                      {waterPricePerLiter.toFixed(3)} ₺/L
                    </div>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Hesaplanan Kar</label>
                    <div className={styles.settingsValue} style={{ color: '#10b981', fontWeight: '600' }}>
                      {waterSaved > 0 
                        ? `${(waterSaved * waterPricePerLiter).toLocaleString('tr-TR', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })} ₺`
                        : '0.00 ₺'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={styles.settingsCard}>
                <div className={styles.settingsCardHeader}>
                  <div className={styles.settingsCardIcon}></div>
                  <h3>Hesap Bilgileri</h3>
                </div>
                <div className={styles.settingsCardBody}>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>E-posta</label>
                    <div className={styles.settingsValue}>{user?.email || 'Kullanıcı'}</div>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Kullanıcı Adı</label>
                    <div className={styles.settingsValue}>{user?.email?.split('@')[0] || 'Kullanıcı'}</div>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Rol</label>
                    <div className={styles.settingsValue}>{userRole || 'Kullanıcı'}</div>
                  </div>
                  <div className={styles.settingsItem}>
                    <button className={styles.settingsButtonDanger} onClick={logout}>
                      <span></span> Çıkış Yap
                    </button>
                  </div>
                </div>
              </div>
              
              <div className={styles.settingsCard}>
                <div className={styles.settingsCardHeader}>
                  <div className={styles.settingsCardIcon}>ℹ</div>
                  <h3>Hakkında</h3>
                </div>
                <div className={styles.settingsCardBody}>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Versiyon</label>
                    <div className={styles.settingsValue}>1.0.0</div>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Geliştirici</label>
                    <div className={styles.settingsValue}>PRONUVE</div>
                  </div>
                  <div className={styles.settingsItem}>
                    <label className={styles.settingsLabel}>Lisans</label>
                    <div className={styles.settingsValue}>Özel Lisans</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div>İçerik yükleniyor...</div>;
    }
  };

  return (
    <div className={styles.dashboardContainer}>
      <Tooltip />
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <div className={styles.sidebarLogo}>
            <h1 className={styles.logoText}>PRONUVE</h1>
            <p className={styles.logoSubtext}>Çankaya Belediyesi</p>
          </div>
        </div>
        <nav className={styles.sidebarNav}>
          {sections.map((section) => (
            <button
              key={section.id}
              className={`${styles.navItem} ${activeSection === section.id ? styles.navItemActive : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <span className={styles.navLabel}>{section.label}</span>
            </button>
          ))}
          
          {/* Diğerleri Menüsü */}
          <div className={styles.otherMenuContainer}>
            <button
              className={`${styles.navItem} ${showOtherMenu ? styles.navItemActive : ''} ${otherSections.some(s => s.id === activeSection) ? styles.navItemActive : ''}`}
              onClick={() => setShowOtherMenu(!showOtherMenu)}
            >
              <span className={styles.navLabel}>Diğerleri</span>
              <span className={styles.navArrow}>{showOtherMenu ? '▼' : '▶'}</span>
            </button>
            {showOtherMenu && (
              <div className={styles.subMenu}>
                {otherSections.map((section) => (
                  <button
                    key={section.id}
                    className={`${styles.subNavItem} ${activeSection === section.id ? styles.subNavItemActive : ''}`}
                    onClick={() => {
                      setActiveSection(section.id);
                      setShowOtherMenu(false);
                    }}
                  >
                    <span className={styles.navLabel}>{section.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>
        <div className={styles.sidebarFooter}>
          <button className={styles.logoutButton} onClick={logout}>
            <span className={styles.navLabel}>Çıkış Yap</span>
          </button>
        </div>
      </div>
      <div className={styles.mainContent}>
        <div className={styles.contentBody}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ExampleDashboard;
